/* eslint-disable no-empty */
/* eslint-disable react-hooks/exhaustive-deps */
// libraries

import dynamic from 'next/dynamic';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { Fragment, Key, ReactNode, useEffect, useMemo, useState } from 'react';
// ORDEM DAS SEÇÕES: contrato de render da home (ver lib/server/section-order.ts).
// Módulo puro — não puxa nada de servidor, pode vir pro bundle client.
import { DEFAULT_HOME_ORDER, normalizeSectionOrder } from '../lib/server/section-order';
// Skeleton genérico (shimmer leve) usado como loading/placeholder dos blocos
// lazy da home — evita "buracos brancos" enquanto o chunk client-side hidrata.
import SectionSkeleton from '../components/utils/SectionSkeleton';
// REGISTRY (M1): mapas de variantes NOVAS por seção — consultados ANTES dos
// switches legados abaixo. Começam vazios ⇒ diff comportamental NULO.
import { NEW_TOPO, NEW_HERO, NEW_CARD, NEW_RODAPE, NEW_DEPOIMENTO, NEW_EQUIPE, NEW_EMPRESA } from '../components/registry/home';
import { paginaInternaAtelier, siteComPaletaAtelier, varsAtelier } from '../components/pieces/atelier/atelier';
import atInterna from '../components/pieces/atelier/AtelierInterna.module.css';
// Aurora (topo 10) / Panorama (topo 13): seções legadas da home (seleção, cidades, empreendimentos,
// depoimentos, "fundo", populares) na linguagem do modelo por CSS, classe-raiz SÓ atrás do gate.
import auHome from '../components/pieces/aurora-secoes/AuroraHome.module.css';
import pnHome from '../components/pieces/panorama-secoes/PanoramaHome.module.css';
// Horizonte (topo 11): margem lateral ÚNICA da home (régua do modelo) nas seções legadas — classe-raiz SÓ atrás do gate.
import hzHome from '../components/pieces/horizonte-secoes/HorizonteHome.module.css';
import lrHome from '../components/pieces/larissa-secoes/LarissaHome.module.css';
import mtHome from '../components/pieces/metropole-secoes/MetropoleHome.module.css';
import inHome from '../components/pieces/internacional/InternacionalHome.module.css';
// Modelo #13 "Norte" (topo 17, 21/10): sans do modelo na página inteira + régua única (coluna --nt-largura centrada)
// e respiro vertical uniforme nas seções legadas — classe-raiz SÓ atrás do gate. @font-face Outfit/Newsreader.
import ntHome from '../components/pieces/norte/NorteHome.module.css';
import '../components/pieces/norte/NorteFonte.module.css';
// Modelo #14 "Campo" (topo 18, 22/10): Inter na página inteira + fundo de PAPEL + régua única (coluna --cp-largura
// centrada) — classe-raiz SÓ atrás do gate. @font-face Inter.
import cpHome from '../components/pieces/campo/CampoHome.module.css';
import '../components/pieces/campo/CampoFonte.module.css';
import { getVenturesShowcase } from '../components/carousel/ventures-showcase/showcase-data';

// Dynamic Imports para otimizar client-side
const WelcomePieces = dynamic(() => import('../components/pieces/welcome/WelcomePieces'), { ssr: true });
const WelcomePieces2 = dynamic(() => import('../components/pieces/welcome2/WelcomePieces2'), { ssr: true });
const WelcomePieces3 = dynamic(() => import('../components/pieces/welcome3/WelcomePieces'), { ssr: true });
const Topo = dynamic(() => import('../components/pieces/topo/Topo'), { ssr: true });
const Topo2 = dynamic(() => import('../components/pieces/topo2/Topo'), { ssr: true });
// MULTI-REGIÃO: topo "multi-filiais" (modelo_topo = 4) = Topo padrão + barra seletora de região
const TopoRegioes = dynamic(() => import('../components/pieces/topo-regioes/TopoRegioes'), { ssr: true });
// BUSCA POR IA (modelo_busca = 4): hero de frase natural → buscaIa → /imoveis
const WelcomeIa = dynamic(() => import('../components/pieces/welcome-ia/WelcomeIa'), { ssr: true });
// TOPO "Clean central" (modelo_topo = 5): logo centralizada + menu horizontal + sticky compacto
const TopoCentral = dynamic(() => import('../components/pieces/topo-central/TopoCentral'), { ssr: true });
// TOPO "Editorial fixo" (modelo_topo = 6): header fixo hide-on-scroll + menu com sublinhado animado
const TopoEditorial = dynamic(() => import('../components/pieces/topo-editorial/TopoEditorial'), { ssr: true });
// TOPO "Flutuante" (modelo_topo = 7): pílula sticky translúcida com blur, menu central e CTA WhatsApp
const TopoModerno = dynamic(() => import('../components/pieces/topo-moderno/TopoModerno'), { ssr: true });
// HERO "imersivo" (modelo_busca = 5): banner full-height + card de busca flutuante
const WelcomeImersivo = dynamic(() => import('../components/pieces/welcome-imersivo/WelcomeImersivo'), { ssr: true });
// HERO "Split" (modelo_busca = 6): 50/50 landing (CTAs à esquerda + form vertical à direita)
const WelcomeSplit = dynamic(() => import('../components/pieces/welcome-split/WelcomeSplit'), { ssr: true });
// HERO "Editorial com card" (modelo_busca = 7): hero 2 colunas (frase à esquerda + card branco de busca)
const WelcomeEditorial = dynamic(() => import('../components/pieces/welcome-editorial/WelcomeEditorial'), { ssr: true });
// #1456 (cs2451): seção "Soluções para proprietários", logo abaixo do buscador.
// `ssr: true` de propósito — o h2/h3 e os alt precisam sair no HTML do servidor
// (SEO, item 9 do ticket). O chunk só é baixado por quem renderiza a seção.
const ProprietariosPieces = dynamic(() => import('../components/pieces/proprietarios/ProprietariosPieces'), { ssr: true });
// Sugestão #213: formulário curto na home, só de quem está em utils/homeFaleConosco — os demais não baixam o chunk.
const FaleConoscoHome = dynamic(() => import('../components/pieces/fale-conosco/FaleConoscoHome'), { ssr: true });
// ssr:true p/ o banner de cookie entrar no HTML do SSR (era o LCP tardio ~5-9s quando
// client-only). Renderiza no servidor quando politic==='nao' (seed via init_props.initialPolitic).
const PopUpComponent = dynamic(() => import('../components/popUp/PopUpComponent'), { ssr: true });
const OwnersPieces = dynamic(() => import('../components/pieces/owners/OwnersPieces'), { ssr: false });
// INC-0147: mesma peça com SSR, usada SÓ nos tenants de HOME_TEXTO_NO_SSR (os demais seguem com a de cima).
const OwnersPiecesSSR = dynamic(() => import('../components/pieces/owners/OwnersPieces'), { ssr: true });
const BusinessPieces = dynamic(() => import('../components/pieces/business/BusinessPieces'), { ssr: true });
const ApartamentCard = dynamic(() => import('../components/carousel/apartment/ApartamentCard'), { ssr: false, loading: () => <SectionSkeleton height={520} cards={4} /> });
const ApartamentCard1 = dynamic(() => import('../components/carousel/apartment/ApartamentCard1'), { ssr: false, loading: () => <SectionSkeleton height={520} cards={4} /> });
const WhatsAppComponent = dynamic(() => import('../components/fixed/whatsapp/WhatsappComponent'), { ssr: false });
const ChannelChatWidget = dynamic(() => import('../components/fixed/channelChat/ChannelChatWidget'), { ssr: false });
const FavoritosComponent = dynamic(() => import('../components/fixed/favoritos/FavoritosComponent'), { ssr: false });
const Popup = dynamic(() => import('../components/pieces/popup/popup'), { ssr: false });
const Simulador = dynamic(() => import('../components/pieces/simulador/simulador'), { ssr: false });
const DepoimentoCard = dynamic(() => import('../components/carousel/depoimentos/DepoimentosCard'), { ssr: false, loading: () => <SectionSkeleton height={260} cards={3} /> });
const DepoimentoCard2 = dynamic(() => import('../components/carousel/depoimentos/DepoimentosCard2'), { ssr: false, loading: () => <SectionSkeleton height={260} cards={3} /> });
const ManagerCard = dynamic(() => import('../components/carousel/manager/ManagerCard'), { ssr: false, loading: () => <SectionSkeleton height={380} cards={4} /> });
// EQUIPE "Grid destaque" (modelo_equipe = 3): grid de corretores com overlay no hover
const ManagerGrid = dynamic(() => import('../components/cards/manager-grid/ManagerGrid'), { ssr: false, loading: () => <SectionSkeleton height={380} cards={4} /> });
// CONSTRUTORAS PARCEIRAS (modelo_construtoras): vitrine de logos, carrossel ou grade
const ConstrutorasCarousel = dynamic(() => import('../components/carousel/construtoras/ConstrutorasCarousel'), { ssr: false, loading: () => <SectionSkeleton height={220} cards={5} /> });
// EMPREENDIMENTOS: carrossel dos empreendimentos publicados (site.exibir_empreendimento),
// alimentado pelo recorte enxuto do SSR (init_props.home_ventures)
const VenturesCarousel = dynamic(() => import('../components/carousel/ventures/VenturesCarousel'), { ssr: false, loading: () => <SectionSkeleton height={380} cards={4} /> });
// Atelier (topo 12): os empreendimentos entram no MESMO trilho dos carrosséis de imóveis (CardAtelier).
const VenturesAtelier = dynamic(() => import('../components/carousel/ventures/VenturesAtelier'), { ssr: false, loading: () => <SectionSkeleton height={380} cards={4} /> });
// Aurora (topo 10): idem, no trilho do CardAurora (Daniel 14/10 — as regras do modelo 9 no modelo 7).
const VenturesAurora = dynamic(() => import('../components/carousel/ventures/VenturesAurora'), { ssr: false, loading: () => <SectionSkeleton height={380} cards={4} /> });
// Horizonte (topo 11): idem, no trilho do CardHorizonte (Daniel 14/10 — as regras dos modelos 9/7 no modelo 8).
const VenturesHorizonte = dynamic(() => import('../components/carousel/ventures/VenturesHorizonte'), { ssr: false, loading: () => <SectionSkeleton height={380} cards={4} /> });
const VenturesPanorama = dynamic(() => import('../components/carousel/ventures/VenturesPanorama'), { ssr: false, loading: () => <SectionSkeleton height={380} cards={4} /> });
const VenturesLarissa = dynamic(() => import('../components/carousel/ventures/VenturesLarissa'), { ssr: false, loading: () => <SectionSkeleton height={380} cards={4} /> });
// Metrópole (topo 15): idem, na grade do CardMetropole (Daniel 21/10 — personalização em todas as seções).
const VenturesMetropole = dynamic(() => import('../components/carousel/ventures/VenturesMetropole'), { ssr: false, loading: () => <SectionSkeleton height={380} cards={4} /> });
// Modelo #12 "Internacional" (topo 16, 21/10): empreendimentos na MESMA grade/cards da vitrine card:13 — só quem usa baixa o chunk.
const VenturesInternacional = dynamic(() => import('../components/carousel/ventures/VenturesInternacional'), { ssr: false, loading: () => <SectionSkeleton height={380} cards={4} /> });
// Modelo #13 "Norte" (topo 17, 21/10): empreendimentos no MESMO cabeçalho/trilho/card da vitrine card:14 (CardNorte em modoEmpreendimento).
const VenturesNorte = dynamic(() => import('../components/carousel/ventures/VenturesNorte'), { ssr: false, loading: () => <SectionSkeleton height={380} cards={3} /> });
// Modelo #14 "Campo" (topo 18, 23/10): empreendimentos no MESMO cabeçalho/trilho/card da vitrine card:15 (CardCampo em modoEmpreendimento).
const VenturesCampo = dynamic(() => import('../components/carousel/ventures/VenturesCampo'), { ssr: false, loading: () => <SectionSkeleton height={380} cards={3} /> });
// VITRINE DE EMPREENDIMENTOS: bloco editorial (foto + cartão de texto) da MESMA
// seção, com conteúdo curado por tenant — null pra quem não tem vitrine.
const VenturesShowcase = dynamic(() => import('../components/carousel/ventures-showcase/VenturesShowcase'), { ssr: false, loading: () => <SectionSkeleton height={420} cards={2} /> });
// TÍTULO da seção de empreendimentos por tenant. Default "EMPREENDIMENTOS" (o de
// sempre, ~300 sites). O cs2619 usa a seção como VITRINE DE LANÇAMENTOS — o
// conteúdo já é a curadoria do cliente (VENTURES_SO_DESTAQUE em site-props), e o
// carrossel de imóveis homônimo saiu da home —, então o rótulo acompanha.
// ⚠️ enquanto `tbl_sites.titulo_empreendimentos` não existir no legado (DDL
// represada desde 17/08), o título não é editável pelo cliente: é este mapa.
const VENTURES_TITULO_POR_TENANT: Record<number, string> = {
    2619: 'LANÇAMENTOS',
    // cs2593 (Cátia Caires, #1385): título da seção 5 do documento "NOVA HOME" que a
    // cliente enviou em 02/09 — o rótulo do link da seção está em utils/textosSecoesPorSite.ts.
    2593: 'Empreendimentos selecionados',
    // cs2639 (CASAMAIS+, 16/09): títulos de seção em frase — ver utils/textosSecoesPorSite.ts.
    2639: 'Empreendimentos',
    // cs2681 (Cardoso Corretor de Imóveis Rurais, 03/11): pedido do cliente no grupo de implantação.
    2681: 'Empreendimentos Rurais',
};

function tituloVitrineEmpreendimentos(siteId: any): string {
    return VENTURES_TITULO_POR_TENANT[Number(siteId)] || 'EMPREENDIMENTOS';
}

// Tenants que pediram para TIRAR o carrossel de empreendimentos da home SEM desligar o
// `exibir_empreendimento` ("Mostrar seção de empreendimentos" no CRM): esse flag é global e
// também apaga o NOME do empreendimento nos cards e na ficha do imóvel — foi assim que
// o cs1391 achou que os empreendimentos tinham "sumido do cadastro" (INC-0113, #1551, 23/09).
// Aqui some só a seção da home; o resto do site continua mostrando os empreendimentos.
const HOME_SEM_CARROSSEL_EMPREENDIMENTOS = new Set<number>([
    1391, // Casa On (imobiliariacasaon.com.br) — INC-0113 / ticket #1551
]);
// INC-0147 (26/09): o SEO do cliente pediu os blocos de TEXTO abaixo da dobra no HTML do SERVIDOR.
// Com o LazyRender eles saem como <div style="min-height:240px"></div> e o texto (seção institucional
// "Quem somos / O que fazemos / Por que fazemos" = banner_empresa, e o rodapé) fica só no __NEXT_DATA__.
// Aqui, a seção institucional (OwnersPieces) e o rodapé saem no SSR; carrosséis seguem lazy.
// A home é ISR cookieless (isMobile=false no servidor): por isso a seção também aparece no CELULAR
// nestes tenants — senão o servidor a mandaria e o celular a tiraria na hidratação (e o índice do
// Google é mobile-first). Só estes tenants (Daniel 26/09); os ~300 sites seguem com o lazy.
const HOME_TEXTO_NO_SSR = new Set<number>([
    2451, // Unio Imóveis (unioimoveis.com.br) — INC-0147 / ticket #1564
]);
// 28/10 (Daniel): busca da home com Comprar/Alugar em tenant configurado SÓ com Venda
// (TransacaoPriorizada=1). Trocar a configuração poria Aluguel no site inteiro (menu, topo);
// aqui o Aluguel entra só nas opções da busca do hero. Se Aluguel entrar na configuração,
// a lista do tenant já o traz e nada é acrescentado.
const BUSCA_HOME_COM_ALUGUEL = new Set<number>([
    2679, // Brava Imóveis (bravaimoveis.com) — busca igual à do cs2059 (hero 4)
]);
// MULTI-FILIAIS: chips "Condomínios em destaque" logo abaixo da busca do hero.
// Devolve null fora do modo popup — ssr:true porque é conteúdo indexável e o
// recorte por filial já vem determinístico do SSR (init_props.home_ventures).
const CondominiosChips = dynamic(() => import('../components/pieces/condominios-chips/CondominiosChips'), { ssr: true });
// CARD "Foto imersiva" (modelo_card = 4): carrossel de cards 100% foto
const ApartamentImersivo = dynamic(() => import('../components/carousel/apartment/ApartamentImersivo'), { ssr: false, loading: () => <SectionSkeleton height={520} cards={4} /> });
// CARD "Editorial com galeria" (modelo_card = 5): card de cantos retos com galeria interna de fotos
const CardEditorial = dynamic(() => import('../components/carousel/apartment/CardEditorial'), { ssr: false, loading: () => <SectionSkeleton height={520} cards={3} /> });
// DEPOIMENTOS "Grid" (modelo_depoimento = 4): grid dos 6 primeiros (sem carrossel)
const DepoimentosGrid = dynamic(() => import('../components/carousel/depoimentos-grid/DepoimentosGrid'), { ssr: false, loading: () => <SectionSkeleton height={260} cards={3} /> });
// RODAPÉ "Mega footer" (rodape = 4): 4 colunas / accordion mobile
const MegaFooter = dynamic(() => import('../components/pieces/mega-footer/MegaFooter'), { ssr: true });
// RODAPÉ "Minimalista duas faixas" (rodape = 5): logo + menu em cima, direitos + CRECI embaixo
const EditorialFooter = dynamic(() => import('../components/pieces/editorial-footer/EditorialFooter'), { ssr: true });
const NoticePieces = dynamic(() => import('../components/pieces/notice/NoticePieces'), { ssr: false });
const NoveltyPieces = dynamic(() => import('../components/pieces/novelty/NoveltyPieces'), { ssr: false, loading: () => <SectionSkeleton height={300} variant="block" title={false} /> });
const Novidades = dynamic(() => import('../components/pieces/novidades/Novidades'), { ssr: false, loading: () => <SectionSkeleton height={360} variant="block" title={false} /> });
const SelectionCarousel = dynamic(() => import('../components/carousel/selection/SelectionCarousel'), { ssr: false });
const SkeletonApartamentCard= dynamic(() => import('../components/cards/apartament/SkeletonApartamentCard'), { ssr: false });
const SocialNetworks= dynamic(() => import('../components/pieces/socialNetworks/SocialNetworks'), { ssr: false });
const BlogCarousel= dynamic(() => import('../components/carousel/blog/BlogCarousel'), { ssr: false, loading: () => <SectionSkeleton height={320} cards={3} /> });
// Modelo "Horizonte" (topo 11): blog, "Nosso canal", equipe e "Nossa empresa" da home na linguagem do
// modelo, atrás do gate `ehModeloHorizonte(site)` — import() LITERAL, um chunk por peça (ver comentário
// do Horizonte em components/registry/home.ts). Zero diff para qualquer site com modelo_topo ≠ 11.
const BlogHorizonte = dynamic(() => import('../components/pieces/horizonte-secoes/BlogHorizonte'), { ssr: true });
const CanalHorizonte = dynamic(() => import('../components/pieces/horizonte-secoes/CanalHorizonte'), { ssr: true });
const EquipeHorizonte = dynamic(() => import('../components/pieces/horizonte-secoes/EquipeHorizonte'), { ssr: true });
const EmpresaHorizonte = dynamic(() => import('../components/pieces/horizonte-secoes/EmpresaHorizonte'), { ssr: true });
// Modelo #11 "Larissa" (Daniel 15/10): canal/equipe/empresa no visual do modelo — import() LITERAL, um chunk por peça.
const CanalLarissa = dynamic(() => import('../components/pieces/larissa-secoes/CanalLarissa'), { ssr: true });
const EquipeLarissa = dynamic(() => import('../components/pieces/larissa-secoes/EquipeLarissa'), { ssr: true });
const EmpresaLarissa = dynamic(() => import('../components/pieces/larissa-secoes/EmpresaLarissa'), { ssr: true });
// Modelo #12 "Metrópole" (Daniel 21/10): canal/equipe/empresa/blog no visual do modelo — import() LITERAL, um chunk por peça.
const CanalMetropole = dynamic(() => import('../components/pieces/metropole-secoes/CanalMetropole'), { ssr: true });
const EquipeMetropole = dynamic(() => import('../components/pieces/metropole-secoes/EquipeMetropole'), { ssr: true });
const EmpresaMetropole = dynamic(() => import('../components/pieces/metropole-secoes/EmpresaMetropole'), { ssr: true });
const BlogMetropole = dynamic(() => import('../components/carousel/blog/BlogMetropole'), { ssr: true });
// Modelo #12 "Internacional" (topo 16, 21/10): canal/equipe/empresa/blog no visual do modelo — import() LITERAL, um chunk por peça.
const CanalInternacional = dynamic(() => import('../components/pieces/internacional-secoes/CanalInternacional'), { ssr: true });
const EquipeInternacional = dynamic(() => import('../components/pieces/internacional-secoes/EquipeInternacional'), { ssr: true });
const EmpresaInternacional = dynamic(() => import('../components/pieces/internacional-secoes/EmpresaInternacional'), { ssr: true });
const BlogInternacional = dynamic(() => import('../components/carousel/blog/BlogInternacional'), { ssr: false, loading: () => <SectionSkeleton height={560} cards={3} /> });
const DepoimentosInternacional = dynamic(() => import('../components/pieces/internacional-secoes/DepoimentosInternacional'), { ssr: false, loading: () => <SectionSkeleton height={420} cards={1} /> });
// Modelo #13 "Norte" (topo 17, 21/10): canal/equipe/empresa/blog/depoimentos no visual do modelo — import() LITERAL, um chunk por peça.
const CanalNorte = dynamic(() => import('../components/pieces/norte-secoes/CanalNorte'), { ssr: true });
const EquipeNorte = dynamic(() => import('../components/pieces/norte-secoes/EquipeNorte'), { ssr: true });
const EmpresaNorte = dynamic(() => import('../components/pieces/norte-secoes/EmpresaNorte'), { ssr: true });
const BlogNorte = dynamic(() => import('../components/carousel/blog/BlogNorte'), { ssr: false, loading: () => <SectionSkeleton height={520} cards={3} /> });
const DepoimentosNorte = dynamic(() => import('../components/pieces/norte-secoes/DepoimentosNorte'), { ssr: false, loading: () => <SectionSkeleton height={420} cards={1} /> });
// Modelo #14 "Campo" (topo 18, 23/10): canal/equipe/empresa/blog/depoimentos/atalhos no visual do modelo — import() LITERAL, um chunk por peça.
const CanalCampo = dynamic(() => import('../components/pieces/campo-secoes/CanalCampo'), { ssr: true });
const EquipeCampo = dynamic(() => import('../components/pieces/campo-secoes/EquipeCampo'), { ssr: true });
const EmpresaCampo = dynamic(() => import('../components/pieces/campo-secoes/EmpresaCampo'), { ssr: true });
const AtalhosCampo = dynamic(() => import('../components/pieces/campo-secoes/AtalhosCampo'), { ssr: true });
const BlogCampo = dynamic(() => import('../components/carousel/blog/BlogCampo'), { ssr: false, loading: () => <SectionSkeleton height={520} cards={3} /> });
const DepoimentosCampo = dynamic(() => import('../components/pieces/campo-secoes/DepoimentosCampo'), { ssr: false, loading: () => <SectionSkeleton height={420} cards={3} /> });
// Modelo #14 "Campo" (topo 18, 22/10): faixa "Anuncie" logo após a 2ª vitrine de cards — import() LITERAL, chunk próprio.
const AnuncieCampo = dynamic(() => import('../components/pieces/campo-secoes/AnuncieCampo'), { ssr: true });
// Modelos #7 "Aurora" (topo 10) e #10 "Panorama" (topo 13): os mesmos quatro blocos da home na linguagem
// de cada modelo, atrás de `ehModeloAurora(site)` / `ehModeloPanorama(site)` — import() LITERAL, um chunk
// por peça. Zero diff para qualquer site com modelo_topo ∉ {10, 13}.
const BlogAurora = dynamic(() => import('../components/pieces/aurora-secoes/BlogAurora'), { ssr: true });
const CanalAurora = dynamic(() => import('../components/pieces/aurora-secoes/CanalAurora'), { ssr: true });
const EquipeAurora = dynamic(() => import('../components/pieces/aurora-secoes/EquipeAurora'), { ssr: true });
const EmpresaAurora = dynamic(() => import('../components/pieces/aurora-secoes/EmpresaAurora'), { ssr: true });
const BlogPanorama = dynamic(() => import('../components/pieces/panorama-secoes/BlogPanorama'), { ssr: true });
const CanalPanorama = dynamic(() => import('../components/pieces/panorama-secoes/CanalPanorama'), { ssr: true });
const EquipePanorama = dynamic(() => import('../components/pieces/panorama-secoes/EquipePanorama'), { ssr: true });
const EmpresaPanorama = dynamic(() => import('../components/pieces/panorama-secoes/EmpresaPanorama'), { ssr: true });
// blogs:3 — "Larissa" (visual da seção "Coelho em Movimento" do modelo pronto #11). Só quem usa o número baixa o chunk.
const BlogLarissa = dynamic(() => import('../components/carousel/blog/BlogLarissa'), { ssr: false, loading: () => <SectionSkeleton height={560} cards={3} /> });
const FeaturedHomeCarousel = dynamic(() => import('../components/carousel/featured_home/FeaturedHomeCarousel'), { ssr: false });

// About - ja estava como dynamic (ok!)
const AboutPieces = dynamic(() => import('../components/pieces/about/AboutPieces'), { ssr: true });
const AboutPieces1 = dynamic(() => import('../components/pieces/about1/AboutPieces1'), { ssr: true });
const AboutPieces2 = dynamic(() => import('../components/pieces/about1/AboutPieces2'), { ssr: true });
const SelectionCarouselImages = dynamic(() => import('../components/carousel/selection_destaque/SelectionCarousel'), { ssr: false });

// Servicos
import { useAuth, useEmpresaRegiao, useEquipeRegiao, useRegiao, useSiteRegiao } from '../components/context/Info';
import LazyRender from '../components/utils/LazyRender';
import NoveltyPiecesSsr, { bannerHomeTemSsr } from '../components/pieces/novelty/NoveltyPiecesSsr';
import { whatsFlutuanteSempre } from '../utils/whatsFlutuanteSempre';
import { buildRegiaoBootstrapScript, filtraCidadesRegiao } from '../lib/region';
import { getHomeServerSideProps } from '../lib/server/site-props';
import styles from '../styles/Home.module.css';
import { formatSelects, formatSelectsTransacao } from '../utils/FunctionsGlobals';
import { condominioDependenteDe } from '../utils/condominiosDependentes';
import { filtrarTiposDoSelect } from '../utils/tiposBuscaOcultos';
import { montaUrlCarrossel } from '../utils/carrossel-href';
import { carrosselTrazExclusivoECategoria } from '../utils/etiquetaCardPorSite';
import { getFloatingWhatsHref } from '../utils/floatingWhatsHref';
import { getHomeProprietarios } from '../utils/homeProprietarios';
import { getHomeFaleConosco } from '../utils/homeFaleConosco';
import { getHomeScopedStorageKey } from '../utils/homeCache';
import { condominiosStorageName, filtrarCondominiosCidadeFixa } from '../utils/condominiosBusca';
import { trackMetaEvent } from '../utils/metaPixel';
import { runWhenIdle } from '../utils/idle';
import SiteHead from '../utils/siteHead';
import { getWithTTL, setWithTTL } from '../utils/storage';
import { isLocalOrTestHost, normalizeHost } from '../utils/url';
import { textosSecoesDoSite, tituloDaSecao } from '../utils/textosSecoesPorSite';
import { ehModeloHorizonte } from '../components/pieces/horizonte/horizonte';
import { ehModeloAurora, varsAurora } from '../components/pieces/aurora/aurora';
import { ehModeloPanorama, varsPanorama } from '../components/pieces/panorama/panorama';
import { varsMetropole } from '../components/pieces/metropole/metropole';
import { varsInternacional } from '../components/pieces/internacional/internacional';
import { varsNorte } from '../components/pieces/norte/norte';
import { varsCampo } from '../components/pieces/campo/campo';

// MAIN COMPONENT

export const getServerSideProps = getHomeServerSideProps;

const MotionDiv = ({ children }: any) => <div>{children}</div>;

let selectorsServicePromise: Promise<typeof import('../services/selectors')> | null = null;
const loadSelectorsService = () => {
    if (!selectorsServicePromise) {
        selectorsServicePromise = import('../services/selectors');
    }
    return selectorsServicePromise;
};

let propertiesServicePromise: Promise<typeof import('../services/properties')> | null = null;
const loadPropertiesService = () => {
    if (!propertiesServicePromise) {
        propertiesServicePromise = import('../services/properties');
    }
    return propertiesServicePromise;
};

const CARROSSEIS_CACHE_TTL_MS = 15 * 60 * 1000;

const parseCachedPayload = <T,>(value: unknown): T | null => {
    if (value == null) return null;
    if (typeof value === 'string') {
        try {
            return JSON.parse(value) as T;
        } catch {
            return null;
        }
    }
    return value as T;
};

const resolveHomeCarouselQueryHost = (originHost: unknown, siteDomainHost: unknown): string => {
    const normalizedOriginHost = normalizeHost(String(originHost || ''));
    const normalizedSiteDomainHost = normalizeHost(String(siteDomainHost || ''));

    if (normalizedOriginHost && isLocalOrTestHost(normalizedOriginHost) && normalizedSiteDomainHost) {
        return normalizedSiteDomainHost;
    }

    return normalizedOriginHost || normalizedSiteDomainHost;
};

// `isRegiaoPath` = a MESMA home renderizada por /r/<slug> (pages/r/[slug].tsx).
// Só a raiz `/` emite o script de bootstrap de filial — em /r/<slug> a região
// já está no caminho e redirecionar de novo daria loop.
const Home = ({ isRegiaoPath = false }: { isRegiaoPath?: boolean }) => {
    const router = useRouter();
    const { simulador } = router.query;
    const {
        allDataHome,
        favorito,
        setFavorito,
        allViewProperties,
        isMobile1,
        init_props,
        politic,
        setAllCompany,
        allCompany,
        listCaracteristicas,
        allCarrosseis,
        all_cards_home,
        setAllCarrosseis
    } = useAuth();

    // useState
    const valores = {precos_venda: [allDataHome?.precos_venda[0] || null], precos_locacao: [allDataHome?.precos_locacao[0] || null]};
    // MULTI-FILIAIS: a home inteira roda com o `site` da região ativa — contato,
    // vídeo do hero e frase_busca/frase_busca_2 já vêm trocados. Sem região
    // (tenant mono-região) é o MESMO objeto de antes.
    const { regiaoAtiva, regioes, regiaoMatriz, modoPopup } = useRegiao();
    // Atelier (topo 12): as seções legadas da home (seleção, empreendimentos, empresa, equipe, blog,
    // populares) pintam com a paleta do modelo e ganham o CSS de AtelierInterna (.home).
    const site = siteComPaletaAtelier(useSiteRegiao(allDataHome?.site));
    const atelier = paginaInternaAtelier(site);
    // MULTI-FILIAIS: na raiz `/`, quem já escolheu uma filial (cookie) é levado
    // para `/r/<slug>` antes da hidratação — HTML igual para todos, cacheável.
    const regiaoBootstrapScript = useMemo(
        () =>
            modoPopup && !isRegiaoPath
                ? buildRegiaoBootstrapScript(regioes.map((r) => r.slug), regiaoMatriz?.slug || '')
                : '',
        [modoPopup, isRegiaoPath, regioes, regiaoMatriz?.slug]
    );
    const [open, setOpen] = useState(site?.id == 1679 ? true : false);
    const [whatsAppActivated, setWhatsAppActivated] = useState(site?.id == 1679 ? true : false);
    // "Nossa Empresa": a filial ativa troca a foto/título do bloco pelos dela (quando tem).
    const allOwners = useEmpresaRegiao(allDataHome?.banner_empresa);
    const allCards = init_props?.allCards || all_cards_home || [];
    const codsite2 = init_props?.codsite;
    const { codsite } = router.query;
    // EQUIPE: só os corretores da filial da região ativa (região sem filial ou
    // corretor sem filial nenhuma → aparece sempre).
    const allManagersTenant = useMemo(
        () => allDataHome?.parceiros?.filter((v: any) => v.url_foto?.length) || [],
        [allDataHome?.parceiros]
    );
    const allManagers = useEquipeRegiao<any>(allManagersTenant);
    // CONSTRUTORAS: o backend já devolve só quem tem logo — o filtro aqui é a
    // rede de segurança do mesmo contrato (logo vazio = nada a exibir).
    const allConstrutoras = allDataHome?.construtoras?.filter(
        (v: any) => v.url_foto?.length
    ) || [];
    const featuredCarrosseis = init_props?.carrosseis_destaque || [];
    // EMPREENDIMENTOS: recorte enxuto do SSR (máx. 12 itens já ordenados) — ver
    // slimHomeVentures em lib/server/site-props.ts. Nunca a lista inteira do tenant.
    const allVentures = init_props?.home_ventures || [];
    // Vitrine editorial da seção de empreendimentos: conteúdo curado, só para os
    // tenants que a têm configurada (null para todos os outros).
    const venturesShowcase = getVenturesShowcase(site?.id);
    const allDepoimentos = allDataHome?.depoimentos?.filter(
        (v: any) => v.depoimento?.length
    ) || [];
    const dataHome = allDataHome;
    const origin = init_props?.prodOrigin;
    const homeStorageHost = normalizeHost(
        origin || init_props?.origemInit || site?.site || (typeof window !== 'undefined' ? window.location.host : '')
    );
    const homeStorageCodsite = codsite2 || site?.id || 0;
    const homeCondominiosStorageKey = getHomeScopedStorageKey(
        condominiosStorageName(site?.id || codsite2),
        homeStorageHost,
        homeStorageCodsite
    );
    const homeCarouselQueryHost = resolveHomeCarouselQueryHost(init_props?.prodOrigin, site?.site);
    const bannerHome = allDataHome?.allBanners.filter((item: { titulo: string; }) => item.titulo === "home");
    const bannerPopup = allDataHome?.allBanners.filter((item: { titulo: string; }) => item.titulo === "popup");
    const bannerFundo = allDataHome?.allBanners.filter((item: { titulo: string; }) => item.titulo === "fundo");
    // Imagem/vídeo de fundo da faixa de novidades (campo nativo do Site Builder).
    const bannerFundoSecao = allDataHome?.allBanners.filter((item: { titulo: string; }) => item.titulo === "novidades_fundo");
    const redes_sociais = allDataHome?.allBanners.filter((item: { titulo: string; }) => item.titulo === "redes_sociais");
    const bannerTopo = allDataHome?.allBanners.filter((item: { ativo: Boolean; }) => item.ativo === true);
    const bannerPersona = allDataHome?.allBanners.filter((item: { titulo: string; }) => item.titulo === "persona_home");
    const persona_redes = allDataHome?.allBanners.filter((item: { titulo: string; }) => item.titulo === "persona_redes");
    const fundo_blog = allDataHome?.allBanners.filter((item: { titulo: string; }) => item.titulo === "blog");
    // Banner da home ADITIVO por device: "home_mobile" só aparece no mobile e "home_desktop"
    // só no desktop, ambos EMPILHANDO sobre o banner "home" (que aparece nos dois).
    const bannerHomeMobile = allDataHome?.allBanners.filter((item: { titulo: string; }) => item.titulo === "home_mobile");
    const bannerHomeDesktop = allDataHome?.allBanners.filter((item: { titulo: string; }) => item.titulo === "home_desktop");
    const urlBannerHome = bannerHome ? bannerHome.length > 0 ? bannerHome : null : null;
    const homeBannerOverride2523 = "https://imob.sobressai.com.br/Banner/2523/12362.jpg";
    const resolvedUrlBannerHome =
        Number(site?.id) === 2523 && urlBannerHome?.length
            ? urlBannerHome.map((item: any, index: number) => (
                index === 0 ? { ...item, url: homeBannerOverride2523 } : item
            ))
            : urlBannerHome;
    const urlBannerHomeMobile = bannerHomeMobile && bannerHomeMobile.length > 0 ? bannerHomeMobile : null;
    const urlBannerHomeDesktop = bannerHomeDesktop && bannerHomeDesktop.length > 0 ? bannerHomeDesktop : null;
    // Renderiza o bloco da home se existir QUALQUER banner de home (base "home" OU só mobile/desktop).
    const hasHomeBannerAny = Boolean((resolvedUrlBannerHome && resolvedUrlBannerHome.length) || urlBannerHomeMobile || urlBannerHomeDesktop);
    const urlBannerPopup = bannerPopup ? bannerPopup.length > 0 ? bannerPopup : null : null;
    const urlBannerFundo = bannerFundo ? bannerFundo.length > 0 ? bannerFundo[0] : null : null;
    const urlBannerFundoSecao = bannerFundoSecao && bannerFundoSecao.length > 0 ? bannerFundoSecao[0] : null;
    const urlBlogFundo = fundo_blog ? fundo_blog.length > 0 ? fundo_blog[0] : "" : "";
    const urlBannerTopo = bannerTopo ? bannerTopo.length > 0 ? bannerTopo[0] : null : null;
    const urlBannerPersona = bannerPersona ? bannerPersona.length > 0 ? bannerPersona[0] : null : null;
    const urlBannePersonaRedes = persona_redes ? persona_redes.length > 0 ? persona_redes[0] : null : null;
    const urlBannerRedesSociais = redes_sociais ? redes_sociais.length > 0 ? redes_sociais[0] : null : null;
    const [companyInputActual, setCompanyInputActual] = useState('');
    const [fullScreen, setFullScreen] = useState(0);
    const [buscaAvancada, setBuscaAvancada] = useState(false);

    const [fullScreenPopup, setFullScreenPopup] = useState(0);
    const [activeDrawer, setActiveDrawer] = useState<any>(null);
    // Opções do select de tipos da busca, já sem os tipos que este site escondeu
    // (utils/tiposBuscaOcultos.ts). Os links de tipo do rodapé e o sitemap seguem
    // com a lista completa — recebem `allDataHome?.tipos` direto.
    const allTypes = filtrarTiposDoSelect(allDataHome?.tipos ? formatSelects(allDataHome?.tipos) : [], codsite2);
    // BUSCA: o select de cidade da home fica restrito às cidades da região ativa
    // (fail-open: recorte vazio → lista inteira, ver filtraCidadesRegiao).
    const allCitiesTenant = allDataHome?.cidades ? formatSelects(allDataHome?.cidades,allDataHome?.site?.cidade_priorizada) : [];
    const allCities = useMemo(
        () => filtraCidadesRegiao<any>(allCitiesTenant, regiaoAtiva),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [allDataHome?.cidades, allDataHome?.site?.cidade_priorizada, regiaoAtiva]
    );
    const allTransacaoTenant = allDataHome?.site?.transacoes ? formatSelectsTransacao(allDataHome?.site?.transacoes) : []
    const allTransacao = BUSCA_HOME_COM_ALUGUEL.has(Number(site?.id))
        && !allTransacaoTenant.some((t: any) => String(t?.value).toLowerCase() === 'aluguel')
        ? [...allTransacaoTenant, { label: 'Aluguel', value: 'Aluguel' }]
        : allTransacaoTenant
    const total_carrosseis = allDataHome?.carrosseis?.length || 0;
    const [carregando_carrosseis, setCarregando_carrosseis] = useState<any>(allCarrosseis?.length == 0 && total_carrosseis > 0 ? false : true);

    useEffect(() => {
        const cleanup = runWhenIdle(() => {
            GetInfoSite();
        }, 1200);
        if(simulador == "1"){
            setFullScreen(1);
        }
        return cleanup;
    }, [simulador]);

    useEffect(() => {
        const cleanup = runWhenIdle(() => {
            if(total_carrosseis > 0 && allCarrosseis?.length == 0){
                GetCachesCarr();
            } else {
                setCarregando_carrosseis(true);
            }
        }, 1200);
        return cleanup;
    }, [allCarrosseis?.length, total_carrosseis, site?.id]);

    useEffect(() => {
        if (site?.id == 1679) {
            setWhatsAppActivated(true);
            setOpen(true);
        }
    }, [site?.id]);

    useEffect(() => {
        if (allCompany?.length == 0 && site?.exibir_empreendimento) {
            const timeOutId = setTimeout(() => {
                const getCondo: any = JSON.parse(localStorage.getItem(homeCondominiosStorageKey) || '[]');
                if (Array.isArray(getCondo) && getCondo.length > 0) {
                    setAllCompany(getCondo);
                    return;
                }
                if (getCondo?.empreendimentos?.length > 0) {
                    setAllCompany(formatSelects(filtrarCondominiosCidadeFixa(getCondo.empreendimentos, site?.id, site?.cidade_busca)));
                    return;
                }
                if (companyInputActual || site?.id == 2427 || site?.id == 2567 || site?.id == 2274 || site?.id == 2262 || site?.id == 2452 || condominioDependenteDe(site)) {
                    GetAllCompany();
                }
            }, 1000);
            return () => clearTimeout(timeOutId);
        }
    }, [companyInputActual, homeCondominiosStorageKey, site?.exibir_empreendimento, site?.id, allCompany?.length]);

    const GetInfoSite = async () => {
        GetBannerNovidades();
        window.localStorage.removeItem(homeCondominiosStorageKey);
    };

    const GetAllCompany = async () => {
        if(allCompany?.length == 0 && site?.exibir_empreendimento ) {
            const { getCompany } = await loadSelectorsService();
            const infoCompany = await getCompany('',origin, codsite2);
            setAllCompany(formatSelects(filtrarCondominiosCidadeFixa(infoCompany?.empreendimentos, site?.id, site?.cidade_busca)));
            localStorage.setItem(homeCondominiosStorageKey, JSON.stringify(infoCompany));
        }
    };

    const GetCachesCarr = async () => {
        const length = allDataHome?.carrosseis?.length || 0;
        if (length <= 0) {
            setCarregando_carrosseis(true);
            return;
        }

        // MULTI-FILIAIS: o fallback client-side dos carrosséis usa o MESMO recorte
        // do SSR (cidades da filial ativa) e chaves de storage com o slug — sem
        // isso o cache de uma filial serviria a outra. Sem região: `null`/'' ⇒
        // queries e chaves idênticas às de hoje.
        const idCidadesRegiao = regiaoAtiva?.cidadeIds?.length ? regiaoAtiva.cidadeIds : null;
        const regiaoStorageSlug = idCidadesRegiao ? regiaoAtiva?.slug || '' : '';

        const cacheKey = getHomeScopedStorageKey(
            'all_carrosseis',
            homeStorageHost,
            homeStorageCodsite,
            length,
            regiaoStorageSlug
        );
        const cachedList = parseCachedPayload<any[]>(getWithTTL<any>(cacheKey));

        if (Array.isArray(cachedList) && cachedList.length > 0) {
            setAllCarrosseis(cachedList);
            setCarregando_carrosseis(true);
            return;
        }

        let all_carrosseis: any[] = [];

        if (allDataHome) {
            const {
                imoveis_novidades,
                properties_carrossel,
                propertiesTop10
            } = await loadPropertiesService();

            for (const element of allDataHome.carrosseis) {
                if (element.personalizado) {
                    const itemCacheKey = getHomeScopedStorageKey(
                        'carrossel',
                        homeStorageHost,
                        homeStorageCodsite,
                        element.id || 0,
                        regiaoStorageSlug
                    );
                    const cachedItems = parseCachedPayload<any[]>(getWithTTL<any>(itemCacheKey));

                    if (Array.isArray(cachedItems) && cachedItems.length > 2) {
                        all_carrosseis.push([element, cachedItems]);
                        continue;
                    }

                    const resultado = await properties_carrossel(
                        element,
                        '',
                        homeCarouselQueryHost || init_props?.prodOrigin,
                        codsite2,
                        idCidadesRegiao,
                        { comExclu: carrosselTrazExclusivoECategoria(site?.id) }
                    );

                    const items = resultado?.imoveis?.imoveis;
                    if (Array.isArray(items) && items.length > 2) {
                        all_carrosseis.push([element, items]);
                        setWithTTL(itemCacheKey, items, CARROSSEIS_CACHE_TTL_MS);
                    }
                    continue;
                }

                if (element.novidades_semana) {
                    const itemCacheKey = getHomeScopedStorageKey(
                        'imoveis_novidade',
                        homeStorageHost,
                        homeStorageCodsite,
                        regiaoStorageSlug
                    );
                    const cachedItems = parseCachedPayload<any[]>(getWithTTL<any>(itemCacheKey));

                    if (Array.isArray(cachedItems) && cachedItems.length > 2) {
                        all_carrosseis.push([element, cachedItems]);
                        continue;
                    }

                    const resultado = await imoveis_novidades(
                        '',
                        homeCarouselQueryHost || init_props?.prodOrigin,
                        codsite2,
                        '',
                        idCidadesRegiao,
                        { comCategoria: carrosselTrazExclusivoECategoria(site?.id) }
                    );
                    const items = resultado?.imoveis_novidades;

                    if (Array.isArray(items) && items.length > 2) {
                        all_carrosseis.push([element, items]);
                        setWithTTL(itemCacheKey, items, CARROSSEIS_CACHE_TTL_MS);
                    }
                    continue;
                }

                if (element.top_10) {
                    const itemCacheKey = getHomeScopedStorageKey(
                        'properties_top10',
                        homeStorageHost,
                        homeStorageCodsite,
                        element?.id || 0,
                        regiaoStorageSlug
                    );
                    const cachedItems = parseCachedPayload<any[]>(getWithTTL<any>(itemCacheKey));

                    if (Array.isArray(cachedItems) && cachedItems.length > 2) {
                        all_carrosseis.push([element, cachedItems]);
                        continue;
                    }

                    const resultado = await propertiesTop10(
                        '',
                        homeCarouselQueryHost || init_props?.prodOrigin,
                        codsite2,
                        '',
                        idCidadesRegiao,
                        element, // Top 10 com uma transação só mostra os destaques daquela (cs2639)
                        { comCategoria: carrosselTrazExclusivoECategoria(site?.id) }
                    );
                    const items = resultado?.imoveis_destaque;

                    if (Array.isArray(items) && items.length > 2) {
                        all_carrosseis.push([element, items]);
                        setWithTTL(itemCacheKey, items, CARROSSEIS_CACHE_TTL_MS);
                    }
                }
            }
        }

        if (Array.isArray(all_carrosseis) && all_carrosseis.length > 0) {
            setAllCarrosseis(all_carrosseis);
            setWithTTL(cacheKey, all_carrosseis, CARROSSEIS_CACHE_TTL_MS);
        }

        setCarregando_carrosseis(true);
    };

    const GetBannerNovidades = async () => {
        const variavelSalva = sessionStorage.getItem('bannerPopup');
        if (variavelSalva != "true" && urlBannerPopup) {
            sessionStorage.setItem('bannerPopup', 'true');
            setFullScreenPopup(1);
        } else {
            setFullScreenPopup(0);
        }
    };

    const handleOpenWhatsApp = () => {
        trackMetaEvent(site, 'Contact', { content_name: 'WhatsApp flutuante', contact_method: 'whatsapp', content_category: 'floating_button' });
        setWhatsAppActivated(true);
        setOpen(true);
    };

    // 2532: o site é excluído do WhatsappComponent (lazy), então o padrão placeholder→ativa→abre
    // exigiria 2 toques. Aqui o FAB vira um <a> nativo que abre o WhatsApp DIRETO em 1 toque.
    // tel1 = número de WhatsApp do tenant; trackLead via import dinâmico (não incha o bundle da home).
    const whats2532Href = `https://api.whatsapp.com/send?phone=55${(site?.Dados?.tel1 || '').replace(/\D/g, '')}&text=${encodeURIComponent(site?.texto_botao_whats || `Olá, estou visitando o site da ${site?.Dados?.empresa || ''}, gostaria de um atendimento personalizado. Pode me ajudar?`)}`;
    const trackWhats2532 = () => {
        trackMetaEvent(site, 'Contact', { content_name: 'WhatsApp flutuante', contact_method: 'whatsapp', content_category: 'floating_button' });
        void import('../components/whatsAppButton/WhatsAppButton')
            .then(({ trackLead }) => trackLead(site, null))
            .catch(() => undefined);
    };
    const whatsFabIcon = (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 1219.547 1225.016"
            className={site?.id == 1874 || site?.id == 2355 ? styles.svg_whats1 : styles.svg_whats}
        >
            <path fill="#E0E0E0" d="M1041.858 178.02C927.206 63.289 774.753.07 612.325 0 277.617 0 5.232 272.298 5.098 606.991c-.039 106.986 27.915 211.42 81.048 303.476L0 1225.016l321.898-84.406c88.689 48.368 188.547 73.855 290.166 73.896h.258.003c334.654 0 607.08-272.346 607.222-607.023.056-162.208-63.052-314.724-177.689-429.463zm-429.533 933.963h-.197c-90.578-.048-179.402-24.366-256.878-70.339l-18.438-10.93-191.021 50.083 51-186.176-12.013-19.087c-50.525-80.336-77.198-173.175-77.16-268.504.111-278.186 226.507-504.503 504.898-504.503 134.812.056 261.519 52.604 356.814 147.965 95.289 95.36 147.728 222.128 147.688 356.948-.118 278.195-226.522 504.543-504.693 504.543z"/>
            <path fill={site?.id == 2470 ? "#928f88" : "#20b038"} d="M27.875 1190.114l82.211-300.18c-50.719-87.852-77.391-187.523-77.359-289.602.133-319.398 260.078-579.25 579.469-579.25 155.016.07 300.508 60.398 409.898 169.891 109.414 109.492 169.633 255.031 169.57 409.812-.133 319.406-260.094 579.281-579.445 579.281-.023 0 .016 0 0 0h-.258c-96.977-.031-192.266-24.375-276.898-70.5l-307.188 80.548z"/><image overflow="visible" opacity=".08" transform="translate(270.984 291.372)"/><path fillRule="evenodd" clipRule="evenodd" fill="#FFF" d="M462.273 349.294c-11.234-24.977-23.062-25.477-33.75-25.914-8.742-.375-18.75-.352-28.742-.352-10 0-26.25 3.758-39.992 18.766-13.75 15.008-52.5 51.289-52.5 125.078 0 73.797 53.75 145.102 61.242 155.117 7.5 10 103.758 166.266 256.203 226.383 126.695 49.961 152.477 40.023 179.977 37.523s88.734-36.273 101.234-71.297c12.5-35.016 12.5-65.031 8.75-71.305-3.75-6.25-13.75-10-28.75-17.5s-88.734-43.789-102.484-48.789-23.75-7.5-33.75 7.516c-10 15-38.727 48.773-47.477 58.773-8.75 10.023-17.5 11.273-32.5 3.773-15-7.523-63.305-23.344-120.609-74.438-44.586-39.75-74.688-88.844-83.438-103.859-8.75-15-.938-23.125 6.586-30.602 6.734-6.719 15-17.508 22.5-26.266 7.484-8.758 9.984-15.008 14.984-25.008 5-10.016 2.5-18.773-1.25-26.273s-32.898-81.67-46.234-111.326z"/>
            <path fill="#FFF" d="M1036.898 176.091C923.562 62.677 772.859.185 612.297.114 281.43.114 12.172 269.286 12.039 600.137 12 705.896 39.633 809.13 92.156 900.13L7 1211.067l318.203-83.438c87.672 47.812 186.383 73.008 286.836 73.047h.255.003c330.812 0 600.109-269.219 600.25-600.055.055-160.343-62.328-311.108-175.649-424.53zm-424.601 923.242h-.195c-89.539-.047-177.344-24.086-253.93-69.531l-18.227-10.805-188.828 49.508 50.414-184.039-11.875-18.867c-49.945-79.414-76.312-171.188-76.273-265.422.109-274.992 223.906-498.711 499.102-498.711 133.266.055 258.516 52 352.719 146.266 94.195 94.266 146.031 219.578 145.992 352.852-.118 274.999-223.923 498.749-498.899 498.749z"/>
        </svg>
    );

    // #843 cs2451: override do FAB flutuante — só retorna não-nulo p/ o site.id '2451' (Tintim),
    // demais ~299 sites permanecem com o comportamento default (null).
    const floatingWhatsHref = getFloatingWhatsHref(site);
    const proprietarios = getHomeProprietarios(site);
    const faleConosco = getHomeFaleConosco(site);

    const renderRodape = () => {
        const hidePopularHome = Number(site?.id) === 2551;

        // REGISTRY (M1): variante nova de rodapé ANTES do switch legado (zero diff se vazio).
        const RodapeNovo = NEW_RODAPE[Number(site?.rodape)];
        if (RodapeNovo) {
            return (
                <RodapeNovo
                    site={site}
                    allCities={allCities}
                    allCitiesTipes={allDataHome?.tipos}
                    codsite={codsite2}
                    origin={origin}
                    isMobile={isMobile1}
                    allRodape={allDataHome?.rodape}
                    favorito={favorito}
                    hidePopular={hidePopularHome}
                />
            );
        }

        switch (site?.rodape) {
            case 1:
                return (
                    <MotionDiv
                        initial={{ opacity: 0, y: 40 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
                        viewport={{ once: true, amount: 0.22 }}
                    >
                        <AboutPieces1 site={site} allCities={allCities} allCitiesTipes={allDataHome?.tipos} codsite={codsite2} origin={origin} isMobile={isMobile1} allRodape={allDataHome?.rodape} favorito={favorito} hidePopular={hidePopularHome}/>
                    </MotionDiv>
                );
            case 2:
                return (
                    <AboutPieces allRodape={allDataHome?.rodape} site={site} allCities={allCities} allCitiesTipes={allDataHome?.tipos} codsite={codsite2} origin={origin} isMobile={isMobile1} hidePopular={hidePopularHome}/>
                );
            case 3:
                return (
                    <AboutPieces2 site={site} allRodape={allDataHome?.rodape} hidePopular={hidePopularHome}/>
                );
            case 4:
                return (
                    <MegaFooter
                        site={site}
                        allCities={allCities}
                        codsite={codsite2}
                        origin={origin}
                        isMobile={isMobile1}
                        favorito={favorito}
                        allRodape={allDataHome?.rodape}
                        hidePopular={hidePopularHome}
                        fallback={
                            <AboutPieces1 site={site} allCities={allCities} allCitiesTipes={allDataHome?.tipos} codsite={codsite2} origin={origin} isMobile={isMobile1} allRodape={allDataHome?.rodape} favorito={favorito} hidePopular={hidePopularHome}/>
                        }
                    />
                );
            case 5:
                return (
                    <EditorialFooter
                        site={site}
                        allCities={allCities}
                        codsite={codsite2}
                        origin={origin}
                        isMobile={isMobile1}
                        favorito={favorito}
                        allRodape={allDataHome?.rodape}
                        hidePopular={hidePopularHome}
                        fallback={
                            <AboutPieces1 site={site} allCities={allCities} allCitiesTipes={allDataHome?.tipos} codsite={codsite2} origin={origin} isMobile={isMobile1} allRodape={allDataHome?.rodape} favorito={favorito} hidePopular={hidePopularHome}/>
                        }
                    />
                );
            default:
            return (
                <MotionDiv
                    initial={{ opacity: 0, y: 40 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
                    viewport={{ once: true, amount: 0.22 }}
                >
                    <AboutPieces1 site={site} allCities={allCities} allCitiesTipes={allDataHome?.tipos} codsite={codsite2} origin={origin} isMobile={isMobile1} allRodape={allDataHome?.rodape} favorito={favorito} hidePopular={hidePopularHome}/>
                </MotionDiv>
            );
        }
    }

    const renderCarrosselCard = (carrossel: any, idx: Key | null | undefined) => {
        // O href do título (e do "Ver todos") espelha os filtros do PRÓPRIO carrossel —
        // transação, cidade, bairro e tipo. Ver utils/carrossel-href.
        const urlCarrossel = montaUrlCarrossel(carrossel?.[0], {
            site,
            items: carrossel?.[1],
            cidades: allDataHome?.cidades,
            tipos: allDataHome?.tipos,
        });
        // REGISTRY (M1): variante nova de card ANTES do switch legado (zero diff se vazio).
        // Título do carrossel: nome cadastrado (ou "CARROSSEL N"). Quem pediu títulos em frase
        // (utils/textosSecoesPorSite — cs2639) recebe "Destaques de vendas"; os demais, o texto intacto.
        const tituloCarrossel = tituloDaSecao(site?.id, carrossel[0]?.titulo ? carrossel[0]?.titulo : `CARROSSEL ${allCarrosseis?.length}`);
        const CardNovo = NEW_CARD[Number(site?.modelo_card)];
        if (CardNovo) {
            return (
                <CardNovo
                    key={idx}
                    slides={carrossel[1]}
                    url={urlCarrossel}
                    title={tituloCarrossel}
                    site={site}
                    favorito={favorito || []}
                    setFavorito={setFavorito}
                    codsite={codsite}
                    is_mobile={isMobile1}
                    cores={carrossel[0]}
                    allViewProperties={allViewProperties}
                />
            );
        }
        switch (site?.modelo_card) {
            case 1:
            return (
                <ApartamentCard
                    key={idx}
                    slides={carrossel[1]}
                    url={urlCarrossel}
                    title={tituloCarrossel}
                    site={site}
                    favorito={favorito || []}
                    setFavorito={setFavorito}
                    codsite={codsite}
                    is_mobile={isMobile1}
                    allViewProperties={allViewProperties}
                />
            );
            case 2:
                return (
                    <ApartamentCard1
                        key={idx}
                        slides={carrossel[1]}
                        url={urlCarrossel}
                        title={tituloCarrossel}
                        site={site}
                        favorito={favorito || []}
                        setFavorito={setFavorito}
                        codsite={codsite}
                        is_mobile={isMobile1}
                        cores={carrossel[0]}
                        allViewProperties={allViewProperties}
                    />
                );
            case 3:
                return (
                    <ApartamentCard1
                        key={idx}
                        slides={carrossel[1]}
                        url={urlCarrossel}
                        title={tituloCarrossel}
                        site={site}
                        favorito={favorito || []}
                        setFavorito={setFavorito}
                        codsite={codsite}
                        is_mobile={isMobile1}
                        cores={carrossel[0]}
                        allViewProperties={allViewProperties}
                    />
                );
            case 4:
                return (
                    <ApartamentImersivo
                        key={idx}
                        slides={carrossel[1]}
                        url={urlCarrossel}
                        title={tituloCarrossel}
                        site={site}
                        favorito={favorito || []}
                        setFavorito={setFavorito}
                        codsite={codsite}
                        is_mobile={isMobile1}
                        cores={carrossel[0]}
                        allViewProperties={allViewProperties}
                    />
                );
            case 5:
                return (
                    <CardEditorial
                        key={idx}
                        slides={carrossel[1]}
                        url={urlCarrossel}
                        title={tituloCarrossel}
                        site={site}
                        favorito={favorito || []}
                        setFavorito={setFavorito}
                        codsite={codsite}
                        is_mobile={isMobile1}
                        cores={carrossel[0]}
                        allViewProperties={allViewProperties}
                    />
                );
            default:
                return (
                    <ApartamentCard
                    key={idx}
                    slides={carrossel[1]}
                    url={urlCarrossel}
                    title={tituloCarrossel}
                    site={site}
                    favorito={favorito || []}
                    setFavorito={setFavorito}
                    codsite={codsite}
                    is_mobile={isMobile1}
                    allViewProperties={allViewProperties}
                    />
                );
        }
    };

    // hero padrão (modelo 1/default) — também usado como FALLBACK do hero IA (modelo 4)
    const renderWelcomeDefault = () => (
        <WelcomePieces
            allCities={allCities}
            allTypes={allTypes}
            allTransacao={allTransacao}
            allCompany={allCompany}
            site={site}
            urlBanner={urlBannerTopo ? urlBannerTopo?.url : ""}
            companyInputActual={companyInputActual}
            setCompanyInputActual={setCompanyInputActual}
            setActiveDrawer={setActiveDrawer}
            activeDrawer={activeDrawer}
            favorito={favorito}
            origin={origin}
            codsite={codsite2}
            valores={valores}
            codsite2={codsite}
            isMobile1={isMobile1}
            props={allDataHome}
            allCards={allCards}
        />
    );

    const renderWelcomePieces = () => {
        // REGISTRY (M1): variante nova de hero/busca ANTES do switch legado (zero diff se vazio).
        const HeroNovo = NEW_HERO[Number(site?.modelo_busca)];
        if (HeroNovo) {
            return (
                <HeroNovo
                    allCities={allCities}
                    allTypes={allTypes}
                    allTransacao={allTransacao}
                    allCompany={allCompany}
                    site={site}
                    urlBanner={urlBannerTopo ? urlBannerTopo?.url : ""}
                    companyInputActual={companyInputActual}
                    setCompanyInputActual={setCompanyInputActual}
                    setActiveDrawer={setActiveDrawer}
                    activeDrawer={activeDrawer}
                    favorito={favorito}
                    origin={origin}
                    codsite={codsite2}
                    valores={valores}
                    codsite2={codsite}
                    isMobile1={isMobile1}
                    props={allDataHome}
                    allCards={allCards}
                    allCarrosseis={allCarrosseis}
                />
            );
        }
        switch (site?.modelo_busca) {
            case 1:
            return (
                <WelcomePieces
                    allCities={allCities}
                    allTypes={allTypes}
                    allTransacao={allTransacao}
                    allCompany={allCompany}
                    site={site}
                    urlBanner={urlBannerTopo ? urlBannerTopo?.url : ""}
                    companyInputActual={companyInputActual}
                    setCompanyInputActual={setCompanyInputActual}
                    setActiveDrawer={setActiveDrawer}
                    activeDrawer={activeDrawer}
                    favorito={favorito}
                    origin={origin}
                    codsite={codsite2}
                    valores={valores}
                    codsite2={codsite}
                    isMobile1={isMobile1}
                    props={allDataHome}
                    allCards={allCards}
                />
            );
            case 2:
                return (
                    <WelcomePieces2
                        allCities={allCities}
                        allTypes={allTypes}
                        allTransacao={allTransacao}
                        allCompany={allCompany}
                        site={site}
                        buscaAvancada={buscaAvancada}
                        setBuscaAvancada={setBuscaAvancada}
                        personaSrc={urlBannerPersona ? urlBannerPersona?.url : ""}
                        urlBanner={urlBannerTopo ? urlBannerTopo?.url : ""}
                        companyInputActual={companyInputActual}
                        setCompanyInputActual={setCompanyInputActual}
                        setActiveDrawer={setActiveDrawer}
                        activeDrawer={activeDrawer}
                        favorito={favorito}
                        origin={origin}
                        codsite={codsite2}
                        valores={valores}
                        codsite2={codsite}
                        isMobile1={isMobile1}
                        props={allDataHome}
                        allCards={allCards}
                    />
                );
            case 3:
                return (
                    <WelcomePieces3
                        allCities={allCities}
                        allTypes={allTypes}
                        allTransacao={allTransacao}
                        allCompany={allCompany}
                        site={site}
                        companyInputActual={companyInputActual}
                        setCompanyInputActual={setCompanyInputActual}
                        setActiveDrawer={setActiveDrawer}
                        activeDrawer={activeDrawer}
                        favorito={favorito}
                        origin={origin}
                        codsite={codsite2}
                        valores={valores}
                        codsite2={codsite}
                        isMobile1={isMobile1}
                        props={allDataHome}
                        allCards={allCards}
                    />
                );
            case 4:
                // BUSCA POR IA — se faltarem dados básicos, cai direto no hero padrão;
                // erros de render caem no fallback via ErrorBoundary interno do WelcomeIa.
                if (!site || !allDataHome) return renderWelcomeDefault();
                return (
                    <WelcomeIa
                        allCities={allCities}
                        allTypes={allTypes}
                        allTransacao={allTransacao}
                        site={site}
                        urlBanner={urlBannerTopo ? urlBannerTopo?.url : ""}
                        origin={origin}
                        codsite={codsite2}
                        codsite2={codsite}
                        valores={valores}
                        setActiveDrawer={setActiveDrawer}
                        activeDrawer={activeDrawer}
                        favorito={favorito}
                        props={allDataHome}
                        fallback={renderWelcomeDefault()}
                        allCards={allCards}
                    />
                );
            case 5:
                // HERO IMERSIVO — sem dados básicos cai no hero padrão; erros de render
                // caem no fallback via ErrorBoundary interno do WelcomeImersivo.
                if (!site || !allDataHome) return renderWelcomeDefault();
                return (
                    <WelcomeImersivo
                        allCities={allCities}
                        allTypes={allTypes}
                        allTransacao={allTransacao}
                        site={site}
                        urlBanner={urlBannerTopo ? urlBannerTopo?.url : ""}
                        origin={origin}
                        codsite={codsite2}
                        codsite2={codsite}
                        valores={valores}
                        setActiveDrawer={setActiveDrawer}
                        activeDrawer={activeDrawer}
                        favorito={favorito}
                        props={allDataHome}
                        fallback={renderWelcomeDefault()}
                        allCards={allCards}
                        allCompany={allCompany}
                        companyInputActual={companyInputActual}
                        setCompanyInputActual={setCompanyInputActual}
                    />
                );
            case 6:
                // HERO SPLIT — mesma guarda + fallback.
                if (!site || !allDataHome) return renderWelcomeDefault();
                return (
                    <WelcomeSplit
                        allCities={allCities}
                        allTypes={allTypes}
                        allTransacao={allTransacao}
                        site={site}
                        origin={origin}
                        codsite={codsite2}
                        codsite2={codsite}
                        valores={valores}
                        setActiveDrawer={setActiveDrawer}
                        activeDrawer={activeDrawer}
                        favorito={favorito}
                        props={allDataHome}
                        fallback={renderWelcomeDefault()}
                        allCards={allCards}
                    />
                );
            case 7:
                // HERO EDITORIAL — mesma guarda + fallback do imersivo.
                if (!site || !allDataHome) return renderWelcomeDefault();
                return (
                    <WelcomeEditorial
                        allCities={allCities}
                        allTypes={allTypes}
                        allTransacao={allTransacao}
                        site={site}
                        urlBanner={urlBannerTopo ? urlBannerTopo?.url : ""}
                        origin={origin}
                        codsite={codsite2}
                        codsite2={codsite}
                        valores={valores}
                        setActiveDrawer={setActiveDrawer}
                        activeDrawer={activeDrawer}
                        favorito={favorito}
                        props={allDataHome}
                        fallback={renderWelcomeDefault()}
                        allCards={allCards}
                    />
                );
            default:
                return renderWelcomeDefault();
        }
    };

    const renderFeaturedHomeCarousel = () => {
        if (!featuredCarrosseis?.length) return null;

        return (
            <FeaturedHomeCarousel
                items={featuredCarrosseis}
                codsite={codsite2}
                origin={origin}
                site={site}
            />
        );
    };

    // REGISTRY (M1): variantes novas de topo/equipe/depoimento consultadas ANTES
    // dos ternários legados no JSX abaixo. Vazias ⇒ undefined ⇒ cadeia de sempre.
    const TopoNovo = NEW_TOPO[Number(site?.modelo_topo)];
    // HORIZONTE (topo 11): sem número novo no catálogo, as seções equipe/empresa/blog/canal da home
    // ganham a linguagem do modelo quando não há variante nova registrada para o número do tenant.
    const horizonte = ehModeloHorizonte(site);
    // Modelos #7 "Aurora" (topo 10) e #10 "Panorama" (topo 13): blocos da home no visual do modelo
    // (mesmo esquema do Horizonte). Raiz ganha data-modelo + CSS vars; zero diff com outros topos.
    const aurora = ehModeloAurora(site);
    const panorama = ehModeloPanorama(site);
    // Modelo #11 "Larissa" (topo 14): mesmo esquema (Daniel 15/10). Gate inline (= ehModeloLarissa) para não pôr o
    // módulo larissa.ts no bundle da home de todos os sites.
    const larissa = Number(site?.modelo_topo) === 14;
    // Modelo #12 "Metrópole" (topo 15): mesmo esquema do Larissa — equipe/empresa/canal/blog/empreendimentos no visual do
    // modelo e vars --mt-* na raiz (gate inline = ehModeloMetropole). Zero diff para qualquer site com modelo_topo ≠ 15.
    const metropole = Number(site?.modelo_topo) === 15;
    // Modelo #12 "Internacional" (topo 16): Montserrat na página inteira + régua lateral única (InternacionalHome) e as
    // seções equipe/empresa/canal/blog/empreendimentos no visual do modelo — gate inline (= ehModeloInternacional).
    const internacional = Number(site?.modelo_topo) === 16;
    // Modelo #13 "Norte" (topo 17): sans do modelo na página inteira + régua única (NorteHome) e as seções equipe/empresa/
    // canal/blog/depoimentos/empreendimentos no visual do modelo — gate inline (= ehModeloNorte). Zero diff com outros topos.
    const norte = Number(site?.modelo_topo) === 17;
    // Modelo #14 "Campo" (topo 18): Inter na página inteira + fundo de PAPEL + régua única (CampoHome) e a faixa
    // "Anuncie" entre as vitrines de cards — gate inline (= ehModeloCampo). Zero diff com outros topos.
    const campo = Number(site?.modelo_topo) === 18;
    // Campo: índice do carrossel depois do qual entra a faixa "Anuncie" — após a 2ª vitrine COMUM (o carrossel de
    // destaques vira o slider grande do card:15 e não conta); com menos vitrines, após a última. -1 = sem faixa.
    const idxAnuncieCampo = (() => {
        if (!campo || !(allCarrosseis?.length > 0)) return -1;
        const comuns = allCarrosseis.map((c: any, i: number) => (/destaque/i.test(String(c?.[0]?.titulo || '')) ? -1 : i)).filter((i: number) => i >= 0);
        return comuns.length ? comuns[Math.min(1, comuns.length - 1)] : allCarrosseis.length - 1;
    })();
    const EquipeNovo = NEW_EQUIPE[Number(site?.modelo_equipe)] ?? (horizonte ? EquipeHorizonte : aurora ? EquipeAurora : panorama ? EquipePanorama : larissa ? EquipeLarissa : metropole ? EquipeMetropole : internacional ? EquipeInternacional : norte ? EquipeNorte : campo ? EquipeCampo : undefined);
    const DepoimentoNovo = NEW_DEPOIMENTO[Number(site?.modelo_depoimento)];
    // A variante nova de "Quem somos" traz a arte do banner "home" para DENTRO da
    // seção (largura total, encostada no texto) — por isso, quando ela está ligada,
    // o slot `banner` da home não renderiza: a arte foi MOVIDA, não duplicada.
    // No Horizonte só com a seção "sobre" ligada (banner_home == "1"): sem ela, o banner legado segue.
    // Aurora e Panorama: a MESMA regra do Horizonte (só com banner_home == "1").
    // NEW_EMPRESA (modelo_empresa 3 "com faixa") também: o slot `sobre` só desenha com banner_home == "1",
    // e sem esse gate a arte "home" saía da faixa e não entrava em lugar nenhum (cs1888, 19/10).
    const EmpresaNovo = (site?.banner_home == "1" ? NEW_EMPRESA[Number(site?.modelo_empresa)] : undefined) ?? (horizonte && site?.banner_home == "1" ? EmpresaHorizonte : aurora && site?.banner_home == "1" ? EmpresaAurora : panorama && site?.banner_home == "1" ? EmpresaPanorama : larissa && site?.banner_home == "1" ? EmpresaLarissa : metropole && site?.banner_home == "1" ? EmpresaMetropole : internacional && site?.banner_home == "1" ? EmpresaInternacional : norte && site?.banner_home == "1" ? EmpresaNorte : campo && site?.banner_home == "1" ? EmpresaCampo : undefined);
    // INC-0147: bloco de texto no HTML do servidor só nos tenants de HOME_TEXTO_NO_SSR. O <div> é o mesmo
    // wrapper que o LazyRender deixa depois de montar (sem style) — DOM final igual; nos demais, o lazy de sempre.
    // Função, não componente (ver ORDEM DAS SEÇÕES abaixo).
    const textoNoSsr = HOME_TEXTO_NO_SSR.has(Number(site?.id));
    const lazyOuSsr = (conteudo: ReactNode) => (
        textoNoSsr ? <div>{conteudo}</div> : <LazyRender>{conteudo}</LazyRender>
    );

    // ORDEM DAS SEÇÕES (Builder): ordem em que o miolo da home é renderizado.
    // Precedência: preview do Builder (?tema=...ordem:) > ordem publicada do tenant
    // > default. Tudo passa por normalizeSectionOrder, que NUNCA omite um slot —
    // array velho/torto vira ordem default, não seção sumida do ar.
    const homeOrder = useMemo(
        () => normalizeSectionOrder(
            init_props?.themePreview?.sectionOrder ?? init_props?.sectionOrder ?? null,
            DEFAULT_HOME_ORDER,
        ),
        [init_props?.themePreview?.sectionOrder, init_props?.sectionOrder],
    );
    // SD-0020 (cs1859, vale para todos): quando o bloco "banner" é o PRIMEIRO do miolo da home (só a
    // faixa fina de "selection" pode vir antes), o 1º slide sai no HTML do servidor com `priority`
    // (NoveltyPiecesSsr) em vez de esperar JS + IntersectionObserver + skeleton. Em qualquer outra
    // posição — ou nos tenants com geometria própria — o bloco continua exatamente como era.
    const bannerPrimeiroDoMiolo = useMemo(() => {
        const idx = homeOrder.indexOf('banner');
        return idx >= 0 && homeOrder.slice(0, idx).every((slot) => slot === 'selection');
    }, [homeOrder]);
    const bannerHomeNoSsr = bannerPrimeiroDoMiolo && bannerHomeTemSsr(site, resolvedUrlBannerHome);

    // ORDEM DAS SEÇÕES (Builder): cada bloco do miolo da home vira uma FUNÇÃO
    // (função, não componente novo — componente remonta a árvore e mata o LazyRender/Motion).
    // A sequência vem de `homeOrder`; a default é byte-a-byte a ordem antiga do JSX.
    // Slots com "_" são internos/fixos (overlays e hacks posicionais por tenant).
    const HOME_BLOCKS: Record<string, () => any> = {
        "selection": () => (
            site?.modelo_selection == 2 && (
                    <LazyRender>
                        <div data-builder-section="selection">
                            <SelectionCarousel site={site} allCaract={listCaracteristicas} codsite={codsite} allTipos={allDataHome?.tipos} qtdCondominios={allVentures.length}/>
                        </div>
                    </LazyRender>
                )
        ),
        "banner": () => (
            hasHomeBannerAny && !EmpresaNovo && site?.id != 2052 && site?.id != 2523 && site?.id != 2291 && site?.id != 2226 ? (
                bannerHomeNoSsr ? (
                    <NoveltyPiecesSsr banner={resolvedUrlBannerHome} bannerMobile={urlBannerHomeMobile} bannerDesktop={urlBannerHomeDesktop} site={site}/>
                ) : (
                    <LazyRender>
                        <MotionDiv
                            initial={{ opacity: 0, y: 40 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
                            viewport={{ once: true, amount: 0.22 }}
                        >
                            <NoveltyPieces banner={resolvedUrlBannerHome} bannerMobile={urlBannerHomeMobile} bannerDesktop={urlBannerHomeDesktop} site={site}/>
                        </MotionDiv>
                    </LazyRender>
                )
            ) : null
        ),
        "cidades": () => (
            site?.modelo_cidades == 1 && allDataHome?.allBanners ? (
                    <LazyRender>
                        <div data-builder-section="cidades">
                            <SelectionCarouselImages
                                banners={allDataHome?.allBanners || []}
                                codsite={codsite2}
                                filterTitle="carrossel_destaques"
                            />
                        </div>
                    </LazyRender>
                ) : null
        ),
        "_popup": () => (
            ((urlBannerPopup && urlBannerPopup.length > 0) || site?.id == 2238) && fullScreenPopup ? (
                    <LazyRender>
                        <Popup banner={urlBannerPopup} setFullScram={setFullScreenPopup} site={site}/>
                    </LazyRender>
                ) : null
        ),
        "destaques": () => (
            site?.id != 2551 ? renderFeaturedHomeCarousel() : null
        ),
        "card": () => (
            (allCarrosseis?.length > 0 || (total_carrosseis > 0 && !carregando_carrosseis)) ? (
                    <LazyRender rootMargin="300px 0px">
                        {/* BUILDER: a âncora "card" envolve TODOS os carrosséis de imóveis,
                            não só o 1º. Continua sendo UMA única âncora no DOM (goto-section
                            e buildStableSelector seguem achando a mesma, e o 1º carrossel
                            continua sendo o filho 1 — seletores posicionais já salvos não
                            mudam de alvo). O que muda: o CSS custom do tenant ancorado nela
                            (`[data-builder-section="card"] …`) passa a valer para os
                            carrosséis seguintes também. Antes, quem ajustava "os cards" pelo
                            Builder via o efeito só no primeiro carrossel e os demais ficavam
                            com a cor crua do carrossel (cs2409: título/preço laranja em
                            Venda e Aluguel; cs2609: só a 1ª faixa preta). */}
                        {allCarrosseis?.length > 0 ?
                            <div data-builder-section="card">
                                {allCarrosseis.map((carrossel: any, idx: Key | null | undefined) =>
                                    campo ? (
                                        /* Campo (22/10): a faixa "Anuncie" entra logo após a 2ª vitrine (ou após a última,
                                           se houver menos). Fragment não gera DOM; os demais modelos seguem no ramo de sempre. */
                                        <Fragment key={idx}>
                                            {renderCarrosselCard(carrossel, idx)}
                                            {Number(idx) === idxAnuncieCampo ? <AnuncieCampo site={site} /> : null}
                                        </Fragment>
                                    ) : renderCarrosselCard(carrossel, idx)
                                )}
                            </div>
                        : total_carrosseis > 0 && !carregando_carrosseis &&
                            <div data-builder-section="card">
                                {allDataHome?.carrosseis.map((e, id) => (
                                    <MotionDiv
                                        key={id}
                                        initial={{ opacity: 0, y: 40 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
                                        viewport={{ once: true, amount: 0.22 }}
                                    >
                                        <SkeletonApartamentCard carrossel={e}/>
                                    </MotionDiv>
                                ))}
                            </div>
                        }
                    </LazyRender>
                ) : null
        ),
        "empreendimentos": () => (
            site?.exibir_empreendimento && allVentures?.length
                && !HOME_SEM_CARROSSEL_EMPREENDIMENTOS.has(Number(site?.id)) ? (
                    <LazyRender>
                    <div id="empreendimentos" data-builder-section="empreendimentos">
                        <MotionDiv
                            initial={{ opacity: 0, y: 40 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
                            viewport={{ once: true, amount: 0.22 }}
                        >
                            {atelier ? (
                                <VenturesAtelier
                                    slides={allVentures}
                                    title={tituloVitrineEmpreendimentos(site?.id)}
                                    site={site}
                                    codsite={codsite2}
                                    origin={origin}
                                    verTodosIds={init_props?.home_ventures_ver_todos_ids ?? null}
                                />
                            ) : horizonte ? (
                                <VenturesHorizonte
                                    slides={allVentures}
                                    title={tituloVitrineEmpreendimentos(site?.id)}
                                    site={site}
                                    codsite={codsite2}
                                    origin={origin}
                                    verTodosIds={init_props?.home_ventures_ver_todos_ids ?? null}
                                />
                            ) : aurora ? (
                                <VenturesAurora
                                    slides={allVentures}
                                    title={tituloVitrineEmpreendimentos(site?.id)}
                                    site={site}
                                    codsite={codsite2}
                                    origin={origin}
                                    verTodosIds={init_props?.home_ventures_ver_todos_ids ?? null}
                                />
                            ) : panorama ? (
                                <VenturesPanorama
                                    slides={allVentures}
                                    title={tituloVitrineEmpreendimentos(site?.id)}
                                    site={site}
                                    codsite={codsite2}
                                    origin={origin}
                                    verTodosIds={init_props?.home_ventures_ver_todos_ids ?? null}
                                />
                            ) : larissa ? (
                                <VenturesLarissa
                                    slides={allVentures}
                                    title={tituloVitrineEmpreendimentos(site?.id)}
                                    site={site}
                                    codsite={codsite2}
                                    origin={origin}
                                    verTodosIds={init_props?.home_ventures_ver_todos_ids ?? null}
                                />
                            ) : metropole ? (
                                <VenturesMetropole
                                    slides={allVentures}
                                    title={tituloVitrineEmpreendimentos(site?.id)}
                                    site={site}
                                    codsite={codsite2}
                                    origin={origin}
                                    verTodosIds={init_props?.home_ventures_ver_todos_ids ?? null}
                                />
                            ) : internacional ? (
                                <VenturesInternacional
                                    slides={allVentures}
                                    title={tituloVitrineEmpreendimentos(site?.id)}
                                    site={site}
                                    codsite={codsite2}
                                    origin={origin}
                                    verTodosIds={init_props?.home_ventures_ver_todos_ids ?? null}
                                    is_mobile={Boolean(isMobile1)}
                                />
                            ) : norte ? (
                                <VenturesNorte
                                    slides={allVentures}
                                    title={tituloVitrineEmpreendimentos(site?.id)}
                                    site={site}
                                    codsite={codsite2}
                                    origin={origin}
                                    verTodosIds={init_props?.home_ventures_ver_todos_ids ?? null}
                                    is_mobile={Boolean(isMobile1)}
                                />
                            ) : campo ? (
                                <VenturesCampo
                                    slides={allVentures}
                                    title={tituloVitrineEmpreendimentos(site?.id)}
                                    site={site}
                                    codsite={codsite2}
                                    origin={origin}
                                    verTodosIds={init_props?.home_ventures_ver_todos_ids ?? null}
                                    is_mobile={Boolean(isMobile1)}
                                />
                            ) : (
                                <VenturesCarousel
                                    slides={allVentures}
                                    title={tituloVitrineEmpreendimentos(site?.id)}
                                    site={site}
                                    codsite={codsite2}
                                    origin={origin}
                                    verTodosIds={init_props?.home_ventures_ver_todos_ids ?? null}
                                />
                            )}
                        </MotionDiv>
                        {venturesShowcase ? (
                            <MotionDiv
                                initial={{ opacity: 0, y: 40 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
                                viewport={{ once: true, amount: 0.22 }}
                            >
                                <VenturesShowcase
                                    slides={venturesShowcase}
                                    site={site}
                                    codsite={codsite2}
                                    origin={origin}
                                />
                            </MotionDiv>
                        ) : null}
                    </div>
                    </LazyRender>
                ) : null
        ),
        "redes": () => (
            (urlBannePersonaRedes && site?.exibe_redes_sociais && (allDataHome?.redes_sociais?.length) || site?.id == 2301) && (
                    <LazyRender>
                        <MotionDiv
                            initial={{ opacity: 0, y: 40 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
                            viewport={{ once: true, amount: 0.22 }}
                        >
                            <SocialNetworks site={site} props={init_props} urlBannerRedesSociais={urlBannerRedesSociais?.url} urlBannePersonaRedes={urlBannePersonaRedes?.url}></SocialNetworks>
                        </MotionDiv>
                    </LazyRender>
                )
        ),
        // QUEM SOMOS (OwnersPieces): por padrão do template a seção só aparece no
        // DESKTOP (!isMobile1). Sites que pediram vê-la também no mobile entram como
        // exceção por tenant — 2479 já existia; #1131 acrescenta o 1784 (Conexa).
        // Os demais ~300 sites seguem com o comportamento default.
        "sobre": () => {
            // REGISTRY: variante nova de "Quem somos" ANTES dos gates legados (zero
            // diff se o mapa não tiver o número). Gate próprio de propósito: o legado
            // esconde a seção no celular (exceções por tenant), e a variante nova é a
            // etapa institucional do modelo — vale nos dois tamanhos, como na referência.
            if (EmpresaNovo && site?.banner_home == "1") {
                return (
                    <LazyRender>
                        <MotionDiv
                            initial={{ opacity: 0, y: 40 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
                            viewport={{ once: true, amount: 0.22 }}
                        >
                            <EmpresaNovo
                                allOwners={allOwners}
                                site={site}
                                banner={resolvedUrlBannerHome}
                                bannerMobile={urlBannerHomeMobile}
                                isMobile={isMobile1}
                                setFullScram={setFullScreen}
                                origin={origin}
                            />
                        </MotionDiv>
                    </LazyRender>
                );
            }
            return (
            allOwners?.paragrafo_3 == "2" && site?.banner_home == "1" ? (
                    <LazyRender>
                        <MotionDiv
                            initial={{ opacity: 0, y: 40 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
                            viewport={{ once: true, amount: 0.22 }}
                        >
                            <BusinessPieces
                                site={site}
                                allOwners={allOwners}
                            />
                        </MotionDiv>
                    </LazyRender>
                ) : site?.banner_home == "1" && (allOwners?.paragrafo_1 || allOwners?.subtitulo_1 || allOwners?.linkbanner) && (!isMobile1 || [2479, 1784, 2523, 2628].includes(Number(site?.id)) || textoNoSsr) ? (
                    lazyOuSsr(
                        <MotionDiv
                            initial={{ opacity: 0, y: 40 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
                            viewport={{ once: true, amount: 0.22 }}
                        >
                            {textoNoSsr ? (
                                <OwnersPiecesSSR
                                    allOwners={allOwners}
                                    site={site}
                                    setFullScram={setFullScreen}
                                />
                            ) : (
                                <OwnersPieces
                                    allOwners={allOwners}
                                    site={site}
                                    setFullScram={setFullScreen}
                                />
                            )}
                        </MotionDiv>
                    )
                    ) : null
            );
        },
        "equipe": () => (
            site?.modelo_equipe != 0 && Number(site?.carrossel_corretores) && allManagers.length && site?.id != 1968 ? (
                    <LazyRender>
                    <div id="equipe" data-builder-section="equipe">
                        <MotionDiv
                            initial={{ opacity: 0, y: 40 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
                            viewport={{ once: true, amount: 0.22 }}
                        >
                            {EquipeNovo ? (
                                <EquipeNovo
                                    slides={allManagers}
                                    title={textosSecoesDoSite(site?.id).equipeTitulo || (site?.id == 2433 ? "CORRETOR DE IMÓVEIS" : site?.id == 2098 || site?.id == 2146 ? "ATENDIMENTO EXCLUSIVO" : site?.id == 1959 || site?.id == 2091 ? "FALE CONOSCO" : site?.id == 2122 ? "COMPRA, VENDA E LOCAÇÃO DE IMÓVEIS" : site?.id == 2551 ? "EQUIPE DE VENDAS" : "NOSSA EQUIPE")}
                                    site={site}
                                />
                            ) : site?.modelo_equipe == 3 ? (
                                <ManagerGrid
                                    slides={allManagers}
                                    title={textosSecoesDoSite(site?.id).equipeTitulo || (site?.id == 2433 ? "CORRETOR DE IMÓVEIS" : site?.id == 2098 || site?.id == 2146 ? "ATENDIMENTO EXCLUSIVO" : site?.id == 1959 || site?.id == 2091 ? "FALE CONOSCO" : site?.id == 2122 ? "COMPRA, VENDA E LOCAÇÃO DE IMÓVEIS" : site?.id == 2551 ? "EQUIPE DE VENDAS" : "NOSSA EQUIPE")}
                                    site={site}
                                />
                            ) : (
                                <ManagerCard
                                    slides={allManagers}
                                    title={textosSecoesDoSite(site?.id).equipeTitulo || (site?.id == 2433 ? "CORRETOR DE IMÓVEIS" : site?.id == 2098 || site?.id == 2146 ? "ATENDIMENTO EXCLUSIVO" : site?.id == 1959 || site?.id == 2091 ? "FALE CONOSCO" : site?.id == 2122 ? "COMPRA, VENDA E LOCAÇÃO DE IMÓVEIS" : site?.id == 2551 ? "EQUIPE DE VENDAS" : "NOSSA EQUIPE")}
                                    site={site}
                                />
                            )}
                        </MotionDiv>
                    </div>
                    </LazyRender>
                ) : null
        ),
        // CONSTRUTORAS PARCEIRAS — vitrine de logos (Builder: seção `construtoras`).
        // Dois portões: variante ESCOLHIDA e lista não-vazia. O teste é `> 0`, não
        // `!= 0`, porque a seção nasce DESLIGADA: `null != 0` é true em JS, então
        // `!=` ligaria a seção em todo tenant que nunca a configurou (e no site-graphql
        // ainda sem a coluna, onde o campo chega null). Título vem de config, sem
        // condicional por tenant — a dívida que a seção `equipe` acima carrega.
        "construtoras": () => (
            Number(site?.modelo_construtoras) > 0 && allConstrutoras.length ? (
                <LazyRender>
                    <div id="construtoras" data-builder-section="construtoras">
                        <MotionDiv
                            initial={{ opacity: 0, y: 40 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
                            viewport={{ once: true, amount: 0.22 }}
                        >
                            <ConstrutorasCarousel
                                slides={allConstrutoras}
                                title={site?.titulo_construtoras?.trim() || (internacional || norte || campo ? "Construtoras parceiras" : "CONSTRUTORAS PARCEIRAS")}
                                site={site}
                                variante={Number(site?.modelo_construtoras)}
                            />
                        </MotionDiv>
                    </div>
                </LazyRender>
            ) : null
        ),
        "depoimento": () => (
            allDepoimentos.length && site?.modelo_depoimento != 0 ? (
                    <LazyRender>
                        <div data-builder-section="depoimento">
                            {DepoimentoNovo ? (
                                <DepoimentoNovo
                                    slides={allDepoimentos}
                                    title="DEPOIMENTOS"
                                    site={site}
                                />
                            ) : internacional ? (
                                <DepoimentosInternacional slides={allDepoimentos} title="DEPOIMENTOS" site={site}/>
                            ) : norte ? (
                                <DepoimentosNorte slides={allDepoimentos} title="DEPOIMENTOS" site={site}/>
                            ) : campo ? (
                                <DepoimentosCampo slides={allDepoimentos} title="DEPOIMENTOS" site={site}/>
                            ) : site?.modelo_depoimento == 4 ? (
                                <MotionDiv
                                    initial={{ opacity: 0, y: 40 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
                                    viewport={{ once: true, amount: 0.22 }}
                                >
                                    <DepoimentosGrid
                                        slides={allDepoimentos}
                                        title="DEPOIMENTOS"
                                        site={site}
                                    />
                                </MotionDiv>
                            ) : site?.modelo_depoimento == 2 ? (
                                <MotionDiv
                                    initial={{ opacity: 0, y: 40 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
                                    viewport={{ once: true, amount: 0.22 }}
                                >
                                    <DepoimentoCard2
                                        slides={allDepoimentos}
                                        title="DEPOIMENTOS"
                                        site={site}
                                    />
                                </MotionDiv>
                            ) : (
                                <MotionDiv
                                    initial={{ opacity: 0, y: 40 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
                                    viewport={{ once: true, amount: 0.22 }}
                                >
                                    <DepoimentoCard
                                        slides={allDepoimentos}
                                        title="DEPOIMENTOS"
                                        site={site}
                                    />
                                </MotionDiv>
                            )}
                        </div>
                    </LazyRender>
                ) : null
        ),
        "_destaques-2551": () => (
            site?.id == 2551 ? renderFeaturedHomeCarousel() : null
        ),
        "_banner-2226": () => (
            resolvedUrlBannerHome && resolvedUrlBannerHome.length > 0 && site?.id == 2226 ? (
                    <LazyRender>
                        <MotionDiv
                            initial={{ opacity: 0, y: 40 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
                            viewport={{ once: true, amount: 0.22 }}
                        >
                            <NoveltyPieces banner={resolvedUrlBannerHome} bannerMobile={urlBannerHomeMobile} bannerDesktop={urlBannerHomeDesktop} site={site}/>
                        </MotionDiv>
                    </LazyRender>
                ) : null
        ),
        // Instagram/vídeos: por padrão só no DESKTOP. cs2639 (23/09) pediu "Vídeos em destaque" também no celular.
        "instagram": () => (
            (!isMobile1 || site?.id == 2091 || site?.id == 2422 || site?.id == 2226 || site?.id == 2639) &&(site?.widget_instagram || dataHome?.videos) && politic === 'accepted' ? (
                    <LazyRender>
                        <MotionDiv
                            initial={{ opacity: 0, y: 40 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
                            viewport={{ once: true, amount: 0.22 }}
                        >
                            {(aurora || panorama) && Array.isArray(dataHome?.videos) && (dataHome?.videos?.length ?? 0) > 0 ? (
                                <>
                                    {/* Aurora/Panorama: mesma regra do Horizonte abaixo — Instagram segue no NoticePieces; só a faixa de vídeos muda */}
                                    {site?.widget_instagram ? <NoticePieces site={site} title={site?.id == 1609 ? "FIQUE POR DENTRO": `NOSSO INSTAGRAM ${site?.id == 1559 ? " NH" : ""}`} videos={[]}/> : null}
                                    {aurora ? (
                                        <CanalAurora site={site} title={site?.id == 1609 ? "FIQUE POR DENTRO": `NOSSO INSTAGRAM ${site?.id == 1559 ? " NH" : ""}`} videos={dataHome?.videos}/>
                                    ) : (
                                        <CanalPanorama site={site} title={site?.id == 1609 ? "FIQUE POR DENTRO": `NOSSO INSTAGRAM ${site?.id == 1559 ? " NH" : ""}`} videos={dataHome?.videos}/>
                                    )}
                                </>
                            ) : internacional && Array.isArray(dataHome?.videos) && (dataHome?.videos?.length ?? 0) > 0 ? (
                                <>
                                    {/* Internacional (21/10): mesma regra — Instagram segue no NoticePieces; só a faixa de vídeos muda */}
                                    {site?.widget_instagram ? <NoticePieces site={site} title={site?.id == 1609 ? "FIQUE POR DENTRO": `NOSSO INSTAGRAM ${site?.id == 1559 ? " NH" : ""}`} videos={[]}/> : null}
                                    <CanalInternacional site={site} title={site?.id == 1609 ? "FIQUE POR DENTRO": `NOSSO INSTAGRAM ${site?.id == 1559 ? " NH" : ""}`} videos={dataHome?.videos}/>
                                </>
                            ) : norte && Array.isArray(dataHome?.videos) && (dataHome?.videos?.length ?? 0) > 0 ? (
                                <>
                                    {/* Norte (21/10): mesma regra — Instagram segue no NoticePieces; só a faixa de vídeos muda */}
                                    {site?.widget_instagram ? <NoticePieces site={site} title={site?.id == 1609 ? "FIQUE POR DENTRO": `NOSSO INSTAGRAM ${site?.id == 1559 ? " NH" : ""}`} videos={[]}/> : null}
                                    <CanalNorte site={site} title={site?.id == 1609 ? "FIQUE POR DENTRO": `NOSSO INSTAGRAM ${site?.id == 1559 ? " NH" : ""}`} videos={dataHome?.videos}/>
                                </>
                            ) : campo && Array.isArray(dataHome?.videos) && (dataHome?.videos?.length ?? 0) > 0 ? (
                                <>
                                    {/* Campo (23/10): mesma regra — Instagram segue no NoticePieces; só a faixa de vídeos muda */}
                                    {site?.widget_instagram ? <NoticePieces site={site} title={site?.id == 1609 ? "FIQUE POR DENTRO": `NOSSO INSTAGRAM ${site?.id == 1559 ? " NH" : ""}`} videos={[]}/> : null}
                                    <CanalCampo site={site} title={site?.id == 1609 ? "FIQUE POR DENTRO": `NOSSO INSTAGRAM ${site?.id == 1559 ? " NH" : ""}`} videos={dataHome?.videos}/>
                                </>
                            ) : larissa && Array.isArray(dataHome?.videos) && (dataHome?.videos?.length ?? 0) > 0 ? (
                                <>
                                    {/* Larissa (15/10): mesma regra — Instagram segue no NoticePieces; só a faixa de vídeos muda */}
                                    {site?.widget_instagram ? <NoticePieces site={site} title={site?.id == 1609 ? "FIQUE POR DENTRO": `NOSSO INSTAGRAM ${site?.id == 1559 ? " NH" : ""}`} videos={[]}/> : null}
                                    <CanalLarissa site={site} title={site?.id == 1609 ? "FIQUE POR DENTRO": `NOSSO INSTAGRAM ${site?.id == 1559 ? " NH" : ""}`} videos={dataHome?.videos}/>
                                </>
                            ) : metropole && Array.isArray(dataHome?.videos) && (dataHome?.videos?.length ?? 0) > 0 ? (
                                <>
                                    {/* Metrópole (21/10): mesma regra — Instagram segue no NoticePieces; só a faixa de vídeos muda */}
                                    {site?.widget_instagram ? <NoticePieces site={site} title={site?.id == 1609 ? "FIQUE POR DENTRO": `NOSSO INSTAGRAM ${site?.id == 1559 ? " NH" : ""}`} videos={[]}/> : null}
                                    <CanalMetropole site={site} title={site?.id == 1609 ? "FIQUE POR DENTRO": `NOSSO INSTAGRAM ${site?.id == 1559 ? " NH" : ""}`} videos={dataHome?.videos}/>
                                </>
                            ) : horizonte && Array.isArray(dataHome?.videos) && (dataHome?.videos?.length ?? 0) > 0 ? (
                                <>
                                    {/* o widget do Instagram (quando há) segue no NoticePieces; só a faixa de vídeos muda */}
                                    {site?.widget_instagram ? <NoticePieces site={site} title={site?.id == 1609 ? "FIQUE POR DENTRO": `NOSSO INSTAGRAM ${site?.id == 1559 ? " NH" : ""}`} videos={[]}/> : null}
                                    <CanalHorizonte site={site} title={site?.id == 1609 ? "FIQUE POR DENTRO": `NOSSO INSTAGRAM ${site?.id == 1559 ? " NH" : ""}`} videos={dataHome?.videos}/>
                                </>
                            ) : (
                            <NoticePieces site={site} title={site?.id == 1609 ? "FIQUE POR DENTRO": `NOSSO INSTAGRAM ${site?.id == 1559 ? " NH" : ""}`} videos={dataHome?.videos}/>
                            )}
                        </MotionDiv>
                    </LazyRender>
                ) : null
        ),
        "_banner-2291": () => (
            resolvedUrlBannerHome && resolvedUrlBannerHome.length > 0 && site?.id == 2291 ? (
                    <LazyRender>
                        <MotionDiv
                            initial={{ opacity: 0, y: 40 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
                            viewport={{ once: true, amount: 0.22 }}
                        >
                            <NoveltyPieces banner={resolvedUrlBannerHome} bannerMobile={urlBannerHomeMobile} bannerDesktop={urlBannerHomeDesktop} site={site}/>
                        </MotionDiv>
                    </LazyRender>
                ) : null
        ),
        "fundo": () => (
            (!isMobile1 || site?.id == 2091 || site?.id == 2422) && site?.modelo_fundo == 1 ? (
                    <LazyRender>
                        <div data-builder-section="fundo">
                            <MotionDiv
                                initial={{ opacity: 0, y: 40 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
                                viewport={{ once: true, amount: 0.22 }}
                            >
                                {campo ? (
                                    /* Campo (23/10): os 3 atalhos (Anuncie/Simule/Busca) em cartões do modelo, no papel e na régua */
                                    <AtalhosCampo site={site} setFullScram={setFullScreen} isMobile={isMobile1}/>
                                ) : (
                                    <Novidades isMobile={isMobile1} site={site} setFullScram={setFullScreen} bannerWelcome={urlBannerFundo} bannerFundoSecao={urlBannerFundoSecao}/>
                                )}
                            </MotionDiv>
                        </div>
                    </LazyRender>
                ) : null
        ),
        "_simulador": () => (
            fullScreen && site?.simulador && site?.simulador != undefined ?
                    <Simulador site={site} setFullScram={setFullScreen} flutua={1} origin={origin}/>
                : null
        ),
        "_banner-2052": () => (
            resolvedUrlBannerHome && resolvedUrlBannerHome.length > 0 && (site?.id == 2052 || site?.id == 2523) ?  (
                    <LazyRender>
                        <MotionDiv
                            initial={{ opacity: 0, y: 40 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
                            viewport={{ once: true, amount: 0.22 }}
                        >
                            <NoveltyPieces banner={resolvedUrlBannerHome} site={site} disableHover backgroundColor={site?.id == 2523 ? "#000" : "#fff"}/>
                        </MotionDiv>
                    </LazyRender>
                ) : null
        ),
        "blogs": () => (
            allDataHome?.blogs[0]?.id && site?.modelo_blogs == 2 ? (
                    <LazyRender>
                        <div data-builder-section="blogs">
                            {horizonte ? (
                                <BlogHorizonte site={site} posts={allDataHome?.blogs} bannerUrl={urlBlogFundo} codsite={codsite2} origin={origin}/>
                            ) : aurora ? (
                                <BlogAurora site={site} posts={allDataHome?.blogs} bannerUrl={urlBlogFundo} codsite={codsite2} origin={origin}/>
                            ) : panorama ? (
                                <BlogPanorama site={site} posts={allDataHome?.blogs} bannerUrl={urlBlogFundo} codsite={codsite2} origin={origin}/>
                            ) : metropole ? (
                                <BlogMetropole site={site} posts={allDataHome?.blogs} bannerUrl={urlBlogFundo} codsite={codsite2} origin={origin}/>
                            ) : internacional ? (
                                <BlogInternacional site={site} posts={allDataHome?.blogs} codsite={codsite2} origin={origin} is_mobile={Boolean(isMobile1)}/>
                            ) : norte ? (
                                <BlogNorte site={site} posts={allDataHome?.blogs} codsite={codsite2} origin={origin} is_mobile={Boolean(isMobile1)}/>
                            ) : campo ? (
                                <BlogCampo site={site} posts={allDataHome?.blogs} codsite={codsite2} origin={origin} is_mobile={Boolean(isMobile1)}/>
                            ) : (
                            <BlogCarousel site={site} posts={allDataHome?.blogs} bannerUrl={urlBlogFundo} codsite={codsite2} origin={origin}/>
                            )}
                        </div>
                    </LazyRender>
                ) : allDataHome?.blogs[0]?.id && site?.modelo_blogs == 3 ? (
                    <LazyRender>
                        <div data-builder-section="blogs">
                            <BlogLarissa site={site} posts={allDataHome?.blogs} codsite={codsite2} origin={origin} is_mobile={Boolean(isMobile1)}/>
                        </div>
                    </LazyRender>
                ) : null
        ),
    };

    return (
        <div className={`${styles.container}${atelier ? ` ${atInterna.interna} ${atInterna.home}` : ''}${aurora ? ` ${auHome.home}` : ''}${panorama ? ` ${pnHome.home}` : ''}${horizonte ? ` ${hzHome.home}` : ''}${larissa ? ` ${lrHome.home}` : ''}${metropole ? ` ${mtHome.home}` : ''}${internacional ? ` ${inHome.home}` : ''}${norte ? ` ${ntHome.home}` : ''}${campo ? ` ${cpHome.home}` : ''}`} style={{backgroundColor: internacional || norte ? '#ffffff' : campo ? 'var(--cp-papel, #f4f3ec)' : site?.cor5, ...(atelier ? varsAtelier(site) : {}), ...(aurora ? varsAurora(site) : {}), ...(panorama ? varsPanorama(site) : {}), ...(metropole ? varsMetropole(site) : {}), ...(internacional ? varsInternacional(site) : {}), ...(norte ? varsNorte(site) : {}), ...(campo ? varsCampo(site) : {})}} data-modelo={horizonte ? 'horizonte' : aurora ? 'aurora' : panorama ? 'panorama' : metropole ? 'metropole' : internacional ? 'internacional' : norte ? 'norte' : campo ? 'campo' : undefined}>
            {allDataHome ? <SiteHead allDataHome={allDataHome} pagina={"home"} canonicalHost={init_props?.prodOrigin} /> : null}
            {regiaoBootstrapScript ? (
                <Head>
                    {/* eslint-disable-next-line @next/next/no-sync-scripts */}
                    <script dangerouslySetInnerHTML={{ __html: regiaoBootstrapScript }} />
                </Head>
            ) : null}
            {Number(site?.id) === 2439 && !whatsAppActivated ? <ChannelChatWidget /> : null}
            {site && site?.id != 2049 && !whatsAppActivated && (
                floatingWhatsHref ? (
                    <a
                        className={whatsFlutuanteSempre(site) ? `${styles.whatsFab} ${styles.whatsFabSempre}` : styles.whatsFab}
                        aria-label="Abrir atendimento no WhatsApp"
                        href={floatingWhatsHref}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-no-quicklink
                        data-no-instant
                        data-no-prefetch
                        onClick={trackWhats2532}
                    >
                        {whatsFabIcon}
                    </a>
                ) : Number(site?.id) === 2532 ? (
                    <a
                        className={whatsFlutuanteSempre(site) ? `${styles.whatsFab} ${styles.whatsFabSempre}` : styles.whatsFab}
                        aria-label="Abrir atendimento no WhatsApp"
                        href={whats2532Href}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-no-quicklink
                        data-no-instant
                        data-no-prefetch
                        onClick={trackWhats2532}
                    >
                        {whatsFabIcon}
                    </a>
                ) : (
                    <button
                        type="button"
                        className={site?.id == 1893 ? styles.whatsFabAlt : whatsFlutuanteSempre(site) ? `${styles.whatsFab} ${styles.whatsFabSempre}` : styles.whatsFab}
                        aria-label="Abrir atendimento no WhatsApp"
                        onClick={handleOpenWhatsApp}
                    >
                        {whatsFabIcon}
                    </button>
                )
            )}
            {site && site?.id != 2049 && whatsAppActivated && (
                <WhatsAppComponent site={site} codsite={codsite2} origin={origin} open={open} setOpen={setOpen}/>
            )}
            {favorito?.length > 0 ? (
                <LazyRender>
                    <FavoritosComponent site={site} favorito={favorito} compartilhar={false} codsite={codsite2}/>
                </LazyRender>
            ) : null }
            {/* BUILDER: instrumentação data-builder-section — âncoras estáveis pro
                painel do Builder (goto-section/picker via postMessage, ver
                lib/builder-bridge.ts). Wrapper no call-site pra não tocar os
                componentes de cada bloco. "topo" e "rodape" ficam no root dos
                próprios componentes (Topo/Topo2 e AboutPieces*) pra cobrir
                também as demais páginas que os reusam — ver comentário lá. */}
            {TopoNovo ?
                <TopoNovo site={site} setActiveDrawer={setActiveDrawer} setBuscaAvancada={setBuscaAvancada} {...(internacional || norte || campo ? { home: true } : {})}/>
            : site?.modelo_topo == 7 ?
                <TopoModerno site={site} setActiveDrawer={setActiveDrawer}/>
            : site?.modelo_topo == 6 ?
                <TopoEditorial site={site} setActiveDrawer={setActiveDrawer}/>
            : site?.modelo_topo == 5 ?
                <TopoCentral site={site} setActiveDrawer={setActiveDrawer}/>
            : site?.modelo_topo == 4 ?
                <TopoRegioes site={site} setActiveDrawer={setActiveDrawer}/>
            : site?.modelo_topo == 3 ?
                <Topo2 site={site} setActiveDrawer={setActiveDrawer} setBuscaAvancada={setBuscaAvancada}/>
            :
                <Topo site={site} setActiveDrawer={setActiveDrawer}/>
            }
            <div data-builder-section="busca">
                {renderWelcomePieces()}
            </div>
            <CondominiosChips site={site} ventures={allVentures} codsite={codsite2} origin={origin} />

            {/* #1456 — SOLUÇÕES PARA PROPRIETÁRIOS (allowlist em utils/homeProprietarios).
                Fica FORA do `homeOrder`: a posição pedida é fixa ("logo abaixo do buscador,
                antes dos imóveis"), igual ao próprio `busca`, e um slot novo obrigaria a
                editar os 4 espelhos do contrato de ordem (modelos-site + imobtotal-app/api/web),
                que o prebuild `check-section-order-sync` trava — deploy coordenado de 4
                serviços por causa de um bloco de 1 tenant. Sem LazyRender/MotionDiv de
                propósito: ambos só montam no client e tirariam a seção do HTML do SSR. */}
            {proprietarios ? (
                <ProprietariosPieces site={site} config={proprietarios} />
            ) : null}

            {/* #213 — FALE CONOSCO (allowlist em utils/homeFaleConosco): entra colado no slot escolhido,
                fora do contrato de ordem pelo mesmo motivo do bloco acima. Quem não está na lista
                segue com a árvore idêntica: um filho a mais na Fragment deslocaria o useId dos 300 sites. */}
            {homeOrder.map((slot) => (
                <Fragment key={slot}>
                    {faleConosco?.aposSlot === slot ? (
                        <>
                            {HOME_BLOCKS[slot]?.()}
                            <FaleConoscoHome site={site} config={faleConosco} />
                        </>
                    ) : HOME_BLOCKS[slot]?.()}
                </Fragment>
            ))}

            {lazyOuSsr(renderRodape())}
            {/* Banner de cookies = overlay fixo, deve aparecer IMEDIATAMENTE.
                Sem LazyRender: antes montava tarde (intersection) e virava o
                LCP da home (~7-9s no lighthouse). Mantém ssr:false pq o consent
                é client-only (cookie/localStorage). */}
            {site ? <PopUpComponent site={site}/> : null}
        </div>
    );
};

export default Home;
